import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

import TwoColumnLayout from "../../components/layouts/TwoColumnLayout";
import CommentField from "../../components/comment-field/CommentField";
import type { ExistingAttachedFile, AiAssistSendMeta } from "../../components/comment-field/CommentField";
import ThreadItem from "../../components/thread/ThreadItem";
import type { MembershipForAvatar } from "../../components/thread/ThreadItem";
import TopAppBarHome, {
  TopAppBarFloat,
  TOP_APP_BAR_HOME_HEIGHT,
} from "../../components/top-app-bar/TopAppBarHome";
import { ProjectMenu } from "../../components/project/ProjectMenu";
import { useIsTwoColumn } from "../../hooks/useResponsiveLayout";
import SvgLoadingAnimation from "../../components/loading/index";
import MessageItem from "../../components/message/MessageItem";
import Modal from "../../components/modal/Modal";
import ConfirmModal from "../../components/modal/ConfirmModal";
import Button from "../../components/button/Button";
import DropZoneOverlay from "../../components/drop-zone-overlay/DropZoneOverlay";
import UploadProgressBar from "../../components/upload-progress/UploadProgressBar";
import { useUploadFailureDialog } from "../../components/upload-progress/useUploadFailureDialog";
import ApprovalControl from "../../components/approval-control/ApprovalControl";
import {
  useGetDmThreadsQuery,
  useGetDmThreadDetailQuery,
  IN_PROGRESS_STATUSES,
  COMPLETED_STATUSES,
  dmThreadsApi,
  selectLatestContractFile,
} from "../../app/services/dmThreads";
import type { DmThread } from "../../app/services/dmThreads";
import {
  useGetDmMessagesQuery,
  useLazyGetDmMessagesQuery,
  useLazyGetDmMessageDetailQuery,
  useSendDmMessageMutation,
  useUpdateDmMessageMutation,
  useDeleteDmMessageMutation,
  useMarkDmMessagesAsReadMutation,
  useMarkDmFilesAsDownloadedMutation,
} from "../../app/services/dmMessages";
import type { DmMessage, DmMessageUser } from "../../app/services/dmMessages";
import { dmMessageToMessageData } from "../../utils/messageAdapters";
import { useShortcutAction } from "../../utils/keyboardShortcuts";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import {
  isProducerGroupPosition,
  isClientGroupPosition,
  isPrimaryPosition,
  isSpecialistPosition,
} from "../../utils/memberPositionUtils";
import {
  useGetAllProjectMembershipsQuery,
  useGetProjectMembershipQuery,
} from "../../app/accounts/memberships";
import type { ProjectMembership } from "../../app/accounts/memberships";
import useS3Upload, {
  FileWithPath,
  UploadingFile,
  ValidationError,
} from "../../hooks/useS3Upload";
import { useObservedHeight } from "../../hooks/useObservedHeight";
import { extractS3KeyFromUrl } from "../../utils/s3";
import { MessageData } from "../../types/message";
import { canEditDmMessage, getDmEditCutoff } from "../../utils/dmEditWindow";
import { useTimestampSeek } from "../../hooks/useTimestampSeek";
import {
  requestOfferSubcontractPdf,
  formatPdfExportError,
} from "../../app/services/pdfExports";
import type { SubcontractPdfPreviewState } from "../../components/offer-wizard/SubcontractContractPdfPreview";
import TypingIndicator from "../../components/typing-indicator/TypingIndicator";
import {
  useMessengerReconnect,
  useMessengerSocket,
  useMessengerSubscribe,
} from "../../app/messenger/MessengerSocketContext";
import { useTypingSender } from "../../hooks/useTypingSender";
import { useBatchedCall } from "../../hooks/useBatchedCall";
import type { MessengerIncomingEvent } from "../../types/messengerEvents";
import { extractDetailEntity } from "../../utils/detailResponse";
import { useSyncUserDirectory } from "../../hooks/useSyncUserDirectory";
import type { UserDirectoryUpsert } from "../../app/userDirectory/userDirectorySlice";
import { collectMessengerUsers } from "../../utils/collectMessengerUsers";
import OfferWizardOverlay from "../../components/offer-wizard/OfferWizardOverlay";
import DraftThreadItem from "../../components/offer-wizard/DraftThreadItem";
import OfferReceiveModal from "../../components/offer-wizard/OfferReceiveModal";
import ContractAmendModal, {
  type ContractAmendmentDraft,
} from "../../components/offer-wizard/ContractAmendModal";
import ContractAmendmentReviewModal from "../../components/offer-wizard/ContractAmendmentReviewModal";
import OfferReviewSection from "./components/OfferReviewSection";
import ContractTimelineItem from "../../components/message/ContractTimelineItem";
import ContractAmendedTimelineItem from "../../components/message/ContractAmendedTimelineItem";
import ContractAmendmentPanel from "../../components/message/ContractAmendmentPanel";
import ContractMessageRow from "../../components/message/ContractMessageRow";
import SystemNoticePill from "../../components/message/SystemNoticePill";
import { resolveContractSnapshot } from "../../utils/contractAmendment";
import useElementRect from "../../hooks/useElementRect";
import {
  startDraft,
  openDraft,
  closeDraft,
  discardWizard,
  hydrateDraftFromServer,
  setStep,
  dmThreadKey,
  selectOfferDraftsByProject,
} from "../../app/offerWizard/offerWizardSlice";
import { useScrollDirectionLayoutSync } from "../../hooks/useScrollDirectionLayoutSync";
import {
  useDeleteDmThreadMutation,
  useProposeContractAmendmentMutation,
  useRespondContractAmendmentMutation,
  useUpdateDmThreadMutation,
  useWithdrawContractAmendmentMutation,
} from "../../app/services/dmThreads";
import {
  getContractCardPhase,
  isOfferDraftStatus,
  isOfferDraftingForViewer,
  isOfferReceivePendingForViewer,
} from "../../utils/offerStatus";
import useCanCreateOffer from "../../hooks/useCanCreateOffer";
import ChatThreadItem from "../../components/thread/ChatThreadItem";
import CreateDmThreadModal from "./components/CreateDmThreadModal";
import ChatMessenger from "./components/ChatMessenger";
import { useGetChatThreadsQuery } from "../../app/services/chatThreads";
import type { ChatThread } from "../../app/services/chatThreads";
import { extractErrorMessage } from "../../common/utils";

// フィルターモードの型定義
type FilterMode = "in_progress" | "completed" | "search";

/**
 * TwoColumnLayout を包むラッパー。クラス名は RadioCardGroup の breakOut
 * ステージが `.closest(".dm-two-column-wrapper")` でレイアウト位置を特定する
 * マーカーとして維持する。
 * 高さを親（ProjectWrapper の OutletContainer = flex column）いっぱいに確定
 * させるのが重要: 素の div のままだと高さの連鎖が切れて中のレイアウトが
 * OutletContainer より 64px 高くなり（グローバルヘッダー表示中）、ドキュメント
 * 全体がはみ出してヘッダーの出し入れのたびにウィンドウのスクロールバーが
 * 出たり消えたりし、画面全体が横にズレていた。
 */
const DmTwoColumnWrapper = styled.div.attrs({
  className: "dm-two-column-wrapper",
})`
  height: 100%;
  min-height: 0;
`;

// ===== ThreadList =====

const ThreadListContainer = styled.div`
  padding: 0 0 80px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: var(--soremo-light-gray);
`;

const ThreadListOuter = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* フロートするフィルターバー（TopAppBarFloat）の配置基準 */
  position: relative;
`;

// スレッド一覧のスクロール層。上端はフロートするフィルターバー + オファー
// アクションバーの実測高さ分だけ空け、スクロール中のスレッドはその余白を
// 通過してバナー直下まで描画される（クリップ境界は padding-box のため）。
const ThreadListScroll = styled.div<{ $topInset: number }>`
  flex: 1;
  overflow-y: auto;
  /* スレッド数やカラム高さの変化でスクロールバーが出入りしても幅が変わらないように */
  scrollbar-gutter: stable;
  padding-top: ${({ $topInset }) => $topInset}px;
`;

// 選択中スレッド用の focus アンカー。roving tabindex で Tab 先／Shift+Tab 先として
// 機能させる。視覚的には ThreadItem 側の選択表示で十分なので、
// グローバルに当たる [tabindex="0"]:focus-visible の青 outline / box-shadow を抑止する。
const ThreadItemFocusWrapper = styled.div`
  outline: none;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

interface ThreadListProps {
  selectedThreadId: string | null;
  selectedDraftKey: string | null;
  selectedChatId: string | null;
  onThreadSelect: (threadId: string) => void;
  onDraftSelect: (threadKey: string) => void;
  onChatSelect: (chatId: string) => void;
  threads: DmThread[];
  chatThreads: ChatThread[];
  isLoading: boolean;
  /** セグメントコントロールの現在値（DM 親が controlled で保持） */
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  searchText: string;
  onSearchTextChange: (text: string) => void;
}

const ThreadList: React.FC<ThreadListProps> = ({
  selectedThreadId,
  selectedDraftKey,
  selectedChatId,
  onThreadSelect,
  onDraftSelect,
  onChatSelect,
  threads,
  chatThreads,
  isLoading,
  filterMode,
  onFilterModeChange,
  searchText,
  onSearchTextChange,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  // 2 カラム時はスレッド一覧が左カラム幅止まりのため、右端のプロジェクトメニューは
  // この行に置かず TwoColumnLayout の topRightOverlay（ビューポート右端）へ委譲する
  const isTwoColumn = useIsTwoColumn();

  const { data: currentUserData } = useGetCurrentUserQuery();
  const currentUserId = currentUserData?.data?.id;
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, {
    skip: !projectId,
  });
  const isMasterClient = membershipData?.data?.position === "CLIENT";

  // オファー作成（左カラムからドラフトを起こし、ThreadList に並べる）
  const dispatch = useDispatch();
  const canCreateOffer = useCanCreateOffer({ projectId });
  const drafts = useSelector(selectOfferDraftsByProject(projectId));
  const [deleteDmThread] = useDeleteDmThreadMutation();

  // DM（オファー非依存）の作成可否: クライアント責任者(CLIENT) / プロデューサー責任者(PRODUCER) のみ
  const currentUserPosition = membershipData?.data?.position ?? "";
  const canCreateDm = isPrimaryPosition(currentUserPosition);
  const [showCreateDmModal, setShowCreateDmModal] = useState(false);

  const handleStartDraft = useCallback(() => {
    if (!projectId) return;
    const action = dispatch(startDraft(projectId));
    const newKey = (action as unknown as { payload: { threadKey: string } })
      .payload.threadKey;
    onDraftSelect(newKey);
  }, [dispatch, projectId, onDraftSelect]);

  const handleDiscardDraft = useCallback(
    async (threadKey: string) => {
      // dm:<id> 形式ならサーバ側ソフト削除も依頼してから Redux から消す。
      // draft:<uuid> 形式（サーバ未保存）はローカル削除のみで OK。
      const dmId = threadKey.startsWith("dm:")
        ? threadKey.slice("dm:".length)
        : null;
      if (dmId && projectId) {
        try {
          await deleteDmThread({ project_id: projectId, dm_id: dmId }).unwrap();
        } catch (error) {
          console.warn("[DM handleDiscardDraft] deleteDmThread failed", error);
        }
      }
      dispatch(discardWizard({ threadKey }));
    },
    [dispatch, deleteDmThread, projectId],
  );

  // メンバーシップ取得（本契約のアバタースタック用）
  const { data: membershipsData } = useGetAllProjectMembershipsQuery(
    { projectId: projectId! },
    { skip: !projectId }
  );

  // 本契約（master_contract / dm_client）スレッドの参加者は
  // CLIENT（プロジェクトオーナー）と PRODUCER のみ。
  // REVIEWER / DIRECTOR / ADMIN / SPECIALIST はスレッド参加者ではないので除外し、
  // 表示順は「CLIENT グループ → PRODUCER グループ」に揃える。
  // Array.prototype.sort は stable なので、同グループ内はサーバ返却順を維持する。
  const membershipList: MembershipForAvatar[] = useMemo(() => {
    if (!membershipsData?.data) return [];
    const groupRank = (pos?: string | null) =>
      pos && isClientGroupPosition(pos) ? 0 : 1;
    return membershipsData.data
      .filter((m) => !m.is_invited)
      .filter((m) => !!m.position && isPrimaryPosition(m.position))
      .slice()
      .sort((a, b) => groupRank(a.position) - groupRank(b.position))
      .map((m) => ({
        user_id: m.user_id,
        display_name_jp: m.display_name_jp,
        avatar_url: m.avatar_url,
        position: m.position,
      }));
  }, [membershipsData]);

  // ステータス別のスレッド（進行中 / 完了）。
  // セグメントオプションの有無判定、filteredThreads、モード跨ぎのキーハンドラで共通利用する。
  const inProgressThreads = useMemo(
    () =>
      threads.filter((t) =>
        (IN_PROGRESS_STATUSES as readonly string[]).includes(t.status),
      ),
    [threads],
  );
  const completedThreads = useMemo(
    () =>
      threads.filter((t) =>
        (COMPLETED_STATUSES as readonly string[]).includes(t.status),
      ),
    [threads],
  );

  const dmFilterOptions = useMemo(() => {
    const options: { label: string; value: FilterMode }[] = [];
    // Chat（DM）スレッドはステータスを持たず「進行中」側に表示するため、
    // 進行中オファーが無くても Chat があれば「進行中」セグメントを出す。
    if (inProgressThreads.length > 0 || chatThreads.length > 0) {
      options.push({ label: "進行中", value: "in_progress" });
    }
    if (completedThreads.length > 0) {
      options.push({ label: "完了", value: "completed" });
    }
    return options;
  }, [inProgressThreads.length, completedThreads.length, chatThreads.length]);
  const firstAvailableMode = dmFilterOptions[0]?.value ?? "in_progress";
  const availableValues = useMemo(
    () => dmFilterOptions.map((o) => o.value),
    [dmFilterOptions],
  );

  // 現在のフィルタモードでの表示対象スレッド
  const filteredThreads = useMemo(() => {
    switch (filterMode) {
      case "in_progress":
        return inProgressThreads;
      case "completed":
        return completedThreads;
      case "search": {
        const query = searchText.trim().toLowerCase();
        if (!query) return threads;
        return threads.filter((t) => {
          const scope = (t.scope ?? "").toLowerCase();
          const reqName = (t.requester?.display_name ?? "").toLowerCase();
          const conName = (t.contractor?.display_name ?? "").toLowerCase();
          return (
            scope.includes(query) ||
            reqName.includes(query) ||
            conName.includes(query)
          );
        });
      }
      default:
        return threads;
    }
  }, [threads, filterMode, searchText, inProgressThreads, completedThreads]);

  // オファースレッドと Chat（DM）スレッドを updated_at でマージした表示用リスト。
  // Chat はステータスを持たないため「完了」セグメントには出さず、進行中/検索で表示する。
  const mergedItems = useMemo(() => {
    type Entry =
      | { kind: "offer"; thread: DmThread; updated: string }
      | { kind: "chat"; thread: ChatThread; updated: string };
    const offerEntries: Entry[] = filteredThreads.map((t) => ({
      kind: "offer",
      thread: t,
      updated: t.updated_at,
    }));
    const query = searchText.trim().toLowerCase();
    const showChats = filterMode !== "completed";
    const chatEntries: Entry[] = (showChats ? chatThreads : [])
      .filter((c) => {
        if (filterMode !== "search" || !query) return true;
        const names = c.members
          .map((m) => m.display_name)
          .join(" ")
          .toLowerCase();
        return names.includes(query);
      })
      .map((c) => ({ kind: "chat", thread: c, updated: c.updated_at }));
    return [...offerEntries, ...chatEntries].sort(
      (a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime(),
    );
  }, [filteredThreads, chatThreads, filterMode, searchText]);

  const handleFilterModeChange = useCallback((mode: string) => {
    onFilterModeChange(mode as FilterMode);
  }, [onFilterModeChange]);

  const handleSearchTextChange = useCallback((text: string) => {
    onSearchTextChange(text);
  }, [onSearchTextChange]);

  useEffect(() => {
    // search は threads 件数に依存しない特別モードなので維持する
    if (filterMode === "search") return;
    if (availableValues.includes(filterMode)) return;
    onFilterModeChange(firstAvailableMode);
  }, [filterMode, availableValues, firstAvailableMode, onFilterModeChange]);

  // ArrowUp/ArrowDown によるスレッド移動用の ref。
  // ドラフトと通常スレッドを統合した一次元のナビゲーション列で扱う。
  // 要素キーは "draft:<threadKey>" / "thread:<id>" で識別する。
  const itemRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const pendingFocusRef = useRef<string | null>(null);
  const registerItemRef = useCallback(
    (navKey: string, el: HTMLDivElement | null) => {
      if (el) {
        itemRefsRef.current.set(navKey, el);
      } else {
        itemRefsRef.current.delete(navKey);
      }
    },
    [],
  );

  // ===== 統合ナビゲーション列 =====
  //
  // 左カラム表示順そのまま:「ドラフト → filteredThreads」。
  // 上下キー押下時はこの一次元配列を巡回する。
  type NavItem =
    | { kind: "draft"; key: string }
    | { kind: "thread"; id: string };
  const navKeyOf = (item: NavItem): string =>
    item.kind === "draft" ? `draft:${item.key}` : `thread:${item.id}`;

  const navItems: NavItem[] = useMemo(
    () => [
      ...drafts.map((d) => ({ kind: "draft" as const, key: d.threadKey })),
      ...filteredThreads.map((t) => ({ kind: "thread" as const, id: t.id })),
    ],
    [drafts, filteredThreads],
  );

  const currentNavKey = selectedDraftKey
    ? `draft:${selectedDraftKey}`
    : selectedThreadId
      ? `thread:${selectedThreadId}`
      : null;

  // ペンディング中のフォーカス対象を描画後に適用する。
  useEffect(() => {
    const navKey = pendingFocusRef.current;
    if (!navKey) return;
    const el = itemRefsRef.current.get(navKey);
    if (!el) return;
    pendingFocusRef.current = null;
    el.scrollIntoView({ block: "nearest" });
    el.focus({ preventScroll: true });
  }, [filteredThreads, drafts, selectedThreadId, selectedDraftKey]);

  // window keydown でスレッドを上下移動（CommentField 等の入力中は無効）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        !!target?.isContentEditable
      ) {
        return;
      }

      // オファーウィザードが開いている / フォーカスがカルーセル内にあるときは、
      // 上下キーをカード送りに優先させるためスレッド移動を停止する。
      const isWizardOpen = !!document.querySelector(
        "[data-offer-overlay-scroll]",
      );
      const isInCarousel = !!target?.closest('[role="radiogroup"]');
      if (isWizardOpen || isInCarousel) {
        return;
      }

      if (navItems.length === 0) return;

      const currentIdx = currentNavKey
        ? navItems.findIndex((i) => navKeyOf(i) === currentNavKey)
        : -1;

      // 統合配列の境界判定（ドラフト含む全体での先頭/末尾）。
      const isAtEnd = currentIdx === navItems.length - 1;
      const isAtStart = currentIdx === 0;

      // モード跨ぎの遷移は「通常スレッドの末尾／先頭」かつ「対象モードに
      // スレッドが存在する」場合のみ起こる。ドラフトはモード横断で常時表示なので、
      // モード切替の境界はドラフトを除いた filteredThreads の端で判定する。
      const filteredStartIdx = drafts.length;
      const filteredEndIdx = drafts.length + filteredThreads.length - 1;
      const isAtFilteredEnd =
        currentIdx >= 0 && currentIdx === filteredEndIdx;
      const isAtFilteredStart =
        currentIdx >= 0 && currentIdx === filteredStartIdx;
      if (
        e.key === "ArrowDown" &&
        filterMode === "in_progress" &&
        isAtFilteredEnd &&
        completedThreads.length > 0
      ) {
        e.preventDefault();
        const first = completedThreads[0];
        pendingFocusRef.current = `thread:${first.id}`;
        onFilterModeChange("completed");
        onThreadSelect(first.id);
        return;
      }
      if (
        e.key === "ArrowUp" &&
        filterMode === "completed" &&
        isAtFilteredStart &&
        inProgressThreads.length > 0
      ) {
        e.preventDefault();
        const last = inProgressThreads[inProgressThreads.length - 1];
        pendingFocusRef.current = `thread:${last.id}`;
        onFilterModeChange("in_progress");
        onThreadSelect(last.id);
        return;
      }

      e.preventDefault();

      // 同一モード内（ドラフト含む統合配列）の移動。
      // 未選択時は ArrowDown→先頭 / ArrowUp→末尾 から開始。
      let nextIdx: number;
      if (currentIdx === -1) {
        nextIdx = e.key === "ArrowDown" ? 0 : navItems.length - 1;
      } else {
        nextIdx = currentIdx + (e.key === "ArrowDown" ? 1 : -1);
        // 端を越える場合は何もしない（ループしない既存挙動を踏襲）。
        if (
          (e.key === "ArrowDown" && isAtEnd) ||
          (e.key === "ArrowUp" && isAtStart)
        ) {
          return;
        }
      }

      const next = navItems[nextIdx];
      if (!next) return;

      pendingFocusRef.current = navKeyOf(next);
      if (next.kind === "draft") {
        onDraftSelect(next.key);
      } else {
        onThreadSelect(next.id);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    navItems,
    currentNavKey,
    onThreadSelect,
    onDraftSelect,
    onFilterModeChange,
    filterMode,
    inProgressThreads,
    completedThreads,
    drafts.length,
    filteredThreads.length,
  ]);

  // フロート層（フィルターバー + オファーアクションバー）の実測高さ。
  // アクションバーは権限で有無が変わるため、固定値でなく実測でインセットする。
  const { ref: topBarFloatRef, height: topBarFloatHeight } =
    useObservedHeight<HTMLDivElement>();
  const threadListTopInset = Math.max(
    topBarFloatHeight,
    TOP_APP_BAR_HOME_HEIGHT,
  );

  if (isLoading) {
    return <SvgLoadingAnimation />;
  }

  return (
    <ThreadListOuter>
      {/* フィルターバー（スクロールコンテンツの上にフロート） */}
      <TopAppBarFloat ref={topBarFloatRef}>
        <TopAppBarHome
          options={dmFilterOptions}
          defaultValue={firstAvailableMode}
          value={filterMode}
          onChange={handleFilterModeChange}
          showSearchInput={true}
          onSearchChange={handleSearchTextChange}
          searchPlaceholder="スレッドを検索..."
          rightActions={isTwoColumn ? undefined : <ProjectMenu />}
        />
        {(canCreateOffer || canCreateDm) && (
          <OfferActionBar>
            {canCreateOffer && (
              <Button
                size="small"
                variant="outlined"
                theme="primary"
                leftIcon="forward_to_inbox"
                onClick={handleStartDraft}
              >
                新規オファー
              </Button>
            )}
            {canCreateDm && (
              <Button
                size="small"
                variant="outlined"
                theme="secondary"
                leftIcon="chat"
                onClick={() => setShowCreateDmModal(true)}
              >
                DM
              </Button>
            )}
          </OfferActionBar>
        )}
      </TopAppBarFloat>
      <ThreadListScroll $topInset={threadListTopInset}>
        <ThreadListContainer>
          {drafts.map(({ threadKey, entry }) => (
            <ThreadItemFocusWrapper
              key={threadKey}
              ref={(el) => registerItemRef(`draft:${threadKey}`, el)}
              tabIndex={selectedDraftKey === threadKey ? 0 : -1}
            >
              <DraftThreadItem
                entry={entry}
                isSelected={selectedDraftKey === threadKey}
                onClick={() => onDraftSelect(threadKey)}
                onDiscard={() => handleDiscardDraft(threadKey)}
              />
            </ThreadItemFocusWrapper>
          ))}
          {mergedItems.length > 0 ? (
            mergedItems.map((entry) =>
              entry.kind === "offer" ? (
                <ThreadItemFocusWrapper
                  key={`offer:${entry.thread.id}`}
                  ref={(el) => registerItemRef(`thread:${entry.thread.id}`, el)}
                  tabIndex={selectedThreadId === entry.thread.id ? 0 : -1}
                >
                  <ThreadItem
                    thread={entry.thread}
                    currentUserId={currentUserId}
                    isMasterClient={isMasterClient}
                    isSelected={selectedThreadId === entry.thread.id}
                    onClick={() => onThreadSelect(entry.thread.id)}
                    unreadCount={entry.thread.unread_count}
                    memberships={
                      entry.thread.thread_type === "master_contract"
                        ? membershipList
                        : undefined
                    }
                  />
                </ThreadItemFocusWrapper>
              ) : (
                <ThreadItemFocusWrapper key={`chat:${entry.thread.id}`}>
                  <ChatThreadItem
                    thread={entry.thread}
                    currentUserId={currentUserId}
                    isSelected={selectedChatId === entry.thread.id}
                    onClick={() => onChatSelect(entry.thread.id)}
                  />
                </ThreadItemFocusWrapper>
              ),
            )
          ) : drafts.length === 0 ? (
            <EmptyState>
              {filterMode === "search" && searchText
                ? "検索結果がありません"
                : "表示するスレッドがありません"}
            </EmptyState>
          ) : null}
        </ThreadListContainer>
      </ThreadListScroll>
      {showCreateDmModal && projectId && (
        <CreateDmThreadModal
          projectId={projectId}
          currentUserId={currentUserId}
          currentUserPosition={currentUserPosition}
          onClose={() => setShowCreateDmModal(false)}
          onCreated={(thread) => {
            setShowCreateDmModal(false);
            onChatSelect(thread.id);
          }}
        />
      )}
    </ThreadListOuter>
  );
};

// ===== Messenger（右カラム：メッセージ表示） =====

const BOTTOM_BAR_HEIGHT = 80;

// メッセージと CommentField の間に最低限確保する視覚的マージン。
const MESSENGER_BOTTOM_PADDING_BUFFER = 24;
// ResizeObserver 初回計測前の短い瞬間でも余白が確保されるよう、最低値を保持する。
const MESSENGER_BOTTOM_PADDING_MIN = 120;

const MessengerContainer = styled.div.attrs({
  className: "u-col-center",
})`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

// スレッドの最上部に出すアクションバー（オファー作成エントリーポイント）
// フィルターバーと一緒にスレッド一覧へフロートするため、下を通過する
// スレッドがかすかに透けるグラス背景にする（不透明 #fff だと層が分断される）
const OfferActionBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px) saturate(1.4);
  -webkit-backdrop-filter: blur(12px) saturate(1.4);
  border-bottom: 1px solid var(--soremo-border);
`;

// 右カラムにドラフトを開いたときのオーバーレイ用ラッパ。
// OfferWizardOverlay が `position: absolute; inset: 0;` で展開されるため、
// 親に relative + 高さ100% を与える必要がある。
const DraftRightColumn = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const MessengerContent = styled.div.attrs({
  // u-scrollbar-gutter-stable: メッセージ量やカラム高さの変化でスクロールバーが
  // 出入りしても幅が変わらないように
  className: "u-wrapper u-scrollbar-gutter-stable",
})<{ $bottomPadding: number }>`
  height: 100%;
  overflow-y: auto;
  padding-top: 16px;
  padding-bottom: ${({ $bottomPadding }) => $bottomPadding}px;
  will-change: scroll-position;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const DmActionPanel = styled.div<{ $bottom: number; $left: number; $width: number }>`
  position: fixed;
  bottom: ${({ $bottom }) => $bottom}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  z-index: 4;
  padding: 4px 16px;
  transition: bottom 0.15s linear;
`;

const MessengerPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--soremo-light-gray);
  font-size: 14px;
`;

const MessageEndSpacer = styled.div`
  flex-shrink: 0;
  height: 1px;
`;

/**
 * タイムライン中央の告知行。`MessageItem` が system メッセージを中央寄せする
 * のと同じ枠で、契約アイテムの直後に「契約更新が成立」を出すために使う
 * （初回の「オファー成立」と同じ並びにする）。pill 本体は `SystemNoticePill`。
 */
const TimelineNotice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 8px 0;
`;

// CommentField の上に出す、納品 / 検収 / 差し戻しのワークフローアクション領域
const WorkflowActionRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
`;

const WorkflowErrorText = styled.div.attrs({
  className: "bodytext-11 u-text-green",
})`
  width: 100%;
  text-align: center;
`;

const WorkflowRevertRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

interface MessengerProps {
  thread: DmThread;
  /** contractor 視点で `OfferReceiveModal` を初期表示するかどうか */
  showOfferReceiveModalInitially?: boolean;
  /** 送信者が契約アイテムの「内容を編集」を押したときに親へ編集要求を伝える */
  // eslint-disable-next-line no-unused-vars
  onRequestEditOffer?: (dmId: string) => void;
}

// アップロードモーダルのタイトル
const UploadModalTitle = styled.h3.attrs({
  className: "heading-13",
})`
  margin: 0;
  text-align: center;
  color: var(--soremo-text);
`;

// メッセージを時系列順（古い→新しい）でソートするヘルパー関数
const sortMessagesByDate = (messages: DmMessage[]): DmMessage[] => {
  return [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
};

// 既読処理関連の挙動パラメータ
const READ_DEBOUNCE_MS = 500;
const READ_RETRY_MS = 3000;
const READ_VISIBILITY_THRESHOLD = 0.5;

const Messenger: React.FC<MessengerProps> = ({
  thread,
  showOfferReceiveModalInitially = false,
  onRequestEditOffer,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const threadId = thread.id;
  const threadType = thread.thread_type;

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // CommentField（DmActionPanel）の実高さ。テキストエリアが伸びたときに
  // メッセージエリアの下端が隠れないよう padding-bottom を追従させるため。
  const {
    ref: commentFieldRef,
    height: commentFieldHeight,
  } = useObservedHeight<HTMLDivElement>();

  // Redux
  const scrollState = useSelector((state: { layout: string }) => state.layout);

  // DmActionPanel の横位置をコンテナに合わせる
  const panelRect = useElementRect(containerRef);

  // Data
  const { data: currentUserData } = useGetCurrentUserQuery();
  const currentUserId = currentUserData?.data?.id;
  const { data: membershipData } = useGetProjectMembershipQuery(projectId!, {
    skip: !projectId,
  });
  // 既読グループ判定時、receiver に position が付いていない場合のフォールバック用
  const { data: allMembershipsData } = useGetAllProjectMembershipsQuery(
    { projectId: projectId! },
    { skip: !projectId },
  );
  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    refetch: refetchDmMessages,
  } = useGetDmMessagesQuery(
    { project_id: projectId!, dm_id: threadId, filter: "all" },
    { skip: !projectId || !threadId }
  );
  // Mutations
  const [sendMessage, { isLoading: isSending }] = useSendDmMessageMutation();
  const [updateMessage] = useUpdateDmMessageMutation();
  const [deleteMessage] = useDeleteDmMessageMutation();
  const [markDmMessagesAsRead] = useMarkDmMessagesAsReadMutation();
  const [markDmFilesAsDownloaded] = useMarkDmFilesAsDownloadedMutation();

  // S3 アップロード（purpose はスレッド種別で分岐）。
  // 成果物提出（handleDeliverFiles）から参照するため、ワークフローハンドラより前で定義する。
  const uploadPurpose =
    threadType === "master_contract" ? "dm_client" : "dm_specialist";
  const {
    uploadFiles,
    uploadingFiles,
    isUploading,
    abortFile,
    clearCompleted,
    retryFile,
    retryFiles,
    postProcessLabel,
    withPostProcess,
  } = useS3Upload({
      purpose: uploadPurpose,
      projectId,
      onValidationError: (errors: ValidationError[]) => {
        errors.forEach((error) => {
          console.warn(
            `[DM Upload] バリデーションエラー: ${error.file.name} - ${error.message}`,
          );
        });
      },
      onFileComplete: (file: UploadingFile) => {
        console.log(`[DM Upload] アップロード完了: ${file.file.name}`);
      },
      onError: (file: UploadingFile, error: Error) => {
        console.error(`[DM Upload] エラー: ${file.file.name}`, error);
      },
    });

  // アップロード部分失敗時の「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログ
  const { resolveUploadFailures, uploadFailureDialog } = useUploadFailureDialog({
    retryFiles,
  });

  // Lazy 取得（WS 再接続時の一覧再取得 / 受信時の単体取得に利用）
  const [fetchDmMessages] = useLazyGetDmMessagesQuery();
  const [fetchDmMessageDetail] = useLazyGetDmMessageDetailQuery();

  // ローカル state（サーバから取得後、send/update/delete をマージしていく）
  const [allMessages, setAllMessages] = useState<DmMessage[]>([]);
  const hasInitializedMessages = useRef(false);
  // スレッド開封時に最下部へスクロールしたかどうか（スレッド切替時に false に戻す）
  const didInitialScrollRef = useRef(false);

  // ─── オファー受領モーダル関連 ─────────────────────
  const [isOfferReceiveModalOpen, setIsOfferReceiveModalOpen] = useState(
    showOfferReceiveModalInitially,
  );
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [updateDmThread, { isLoading: isAccepting }] =
    useUpdateDmThreadMutation();

  // ワークフロー（納品 / 検収 / 差し戻し）アクション state
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const [isSubmittingDelivery, setIsSubmittingDelivery] = useState(false);
  const [isCompletingInspection, setIsCompletingInspection] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isRevertConfirmOpen, setIsRevertConfirmOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  // 「成果物を提出 >」から起動する隠しファイル入力。
  const deliveryFileInputRef = useRef<HTMLInputElement>(null);

  // スレッド切替時にモーダル状態をリセット
  useEffect(() => {
    setIsOfferReceiveModalOpen(showOfferReceiveModalInitially);
    setAcceptError(null);
    setWorkflowError(null);
    setIsRevertConfirmOpen(false);
  }, [thread.id, showOfferReceiveModalInitially]);

  // subcontract における viewer の立場（master_contract は本ワークフロー対象外）
  const isContractorViewer =
    thread.thread_type === "subcontract" &&
    !!currentUserId &&
    thread.contractor?.id === currentUserId;
  const isRequesterViewer =
    thread.thread_type === "subcontract" &&
    !!currentUserId &&
    thread.requester?.id === currentUserId;

  // AI 支援機能の出し分け。
  // - チクチクフィルター（送信者/発注側）: master_contract=CLIENT / subcontract=requester
  // - 返信君（受託者側）: master_contract=PRODUCER 系 / subcontract=contractor
  const dmUserPosition = membershipData?.data?.position ?? "";
  const isDmSenderSide =
    (thread.thread_type === "master_contract" && dmUserPosition === "CLIENT") ||
    (thread.thread_type === "subcontract" && isRequesterViewer);
  const isDmReceiverSide =
    (thread.thread_type === "master_contract" && isProducerGroupPosition(dmUserPosition)) ||
    (thread.thread_type === "subcontract" && isContractorViewer);

  const handleAcceptOffer = useCallback(async () => {
    if (!projectId) return;
    setAcceptError(null);
    try {
      await updateDmThread({
        project_id: projectId,
        dm_id: thread.id,
        // 成果物不要のオファーは受諾と同時に検収待ちへ直行する（仕様 S4→S7）
        status:
          thread.requires_deliverable === false
            ? "inspection_pending"
            : "in_progress",
      }).unwrap();
      setIsOfferReceiveModalOpen(false);
      // BE 側で「offer_accepted」system メッセージが投入される。
      // `dmThreadsApi.updateDmThread` は `dmMessages` を invalidate しないので
      // ここで手動 refetch を促し、新しい system メッセージを取り込む。
      // 取得結果は messagesData → useEffect 経由で allMessages に反映される。
      hasInitializedMessages.current = false;
      void refetchDmMessages();
    } catch (error) {
      // RTK Query の unwrap() は Error ではなく { status, data } を投げるため、
      // BE の data.message（エスクロー残高不足の説明など）を拾って表示する。
      console.error("[DM] オファー引受に失敗", error);
      setAcceptError(
        extractErrorMessage(
          error,
          "オファーの引受に失敗しました。時間をおいて再度お試しください。",
        ),
      );
    }
  }, [
    projectId,
    thread.id,
    thread.requires_deliverable,
    updateDmThread,
    refetchDmMessages,
  ]);

  // 納品 / 検収 / 差し戻しの共通遷移処理。遷移時に BE が system メッセージ
  // （delivery_submitted / inspection_completed / escrow_*）を投入するため再取得する。
  const runStatusTransition = useCallback(
    async (
      nextStatus:
        | "inspection_pending"
        | "completed"
        | "in_progress"
        | "closed"
        | "rejected",
    ) => {
      await updateDmThread({
        project_id: projectId!,
        dm_id: thread.id,
        status: nextStatus,
      }).unwrap();
      hasInitializedMessages.current = false;
      void refetchDmMessages();
    },
    [projectId, thread.id, updateDmThread, refetchDmMessages],
  );

  // 「成果物を提出 >」: ファイル選択 → アップロード → メッセージ送信 → 検収待ちへ遷移。
  // 提出のたびに delivery_submitted（テイク N）が投入される。
  const handleDeliverFiles = useCallback(
    async (files: File[]) => {
      if (!projectId || !threadId || files.length === 0) return;
      setWorkflowError(null);
      setIsSubmittingDelivery(true);
      try {
        const filesWithPath: FileWithPath[] = files.map((file) => ({
          file,
          relativePath: file.name,
        }));
        const uploadResults = await uploadFiles(filesWithPath);
        // 失敗があれば「再試行 / 失敗分を除いて続行 / キャンセル」ダイアログで解決
        const hadFailures = uploadResults.some((f) => f.status === "error");
        const resolved = await resolveUploadFailures(uploadResults);
        if (resolved === null) {
          // キャンセル: 成果物提出を中断
          clearCompleted();
          return;
        }
        if (hadFailures) clearCompleted();
        const fileKeys = resolved
          .filter((f) => f.fileKey)
          .map((f) => f.fileKey as string);
        if (fileKeys.length === 0) {
          setWorkflowError(
            "成果物のアップロードに失敗しました。再度お試しください。",
          );
          return;
        }
        // 提出メッセージの送信と検収待ちへの遷移が終わるまでは離脱を引き止める
        await withPostProcess(async () => {
          const result = await sendMessage({
            project_id: projectId,
            dm_id: threadId,
            message: "",
            file_keys: fileKeys,
          }).unwrap();
          if (result.data?.message) {
            const newMessage = result.data.message;
            setAllMessages((prev) =>
              prev.some((m) => m.id === newMessage.id)
                ? prev
                : [...prev, newMessage],
            );
          }
          await runStatusTransition("inspection_pending");
        }, "成果物を提出中");
      } catch (error) {
        console.error("[DM] 成果物提出に失敗", error);
        setWorkflowError(
          extractErrorMessage(
            error,
            "成果物の提出に失敗しました。時間をおいて再度お試しください。",
          ),
        );
      } finally {
        setIsSubmittingDelivery(false);
      }
    },
    [projectId, threadId, uploadFiles, resolveUploadFailures, clearCompleted, sendMessage, withPostProcess, runStatusTransition],
  );

  const handleCompleteInspection = useCallback(async () => {
    if (!projectId) return;
    setWorkflowError(null);
    setIsCompletingInspection(true);
    try {
      await runStatusTransition("completed");
    } catch (error) {
      console.error("[DM] 検収完了に失敗", error);
      setWorkflowError(
        extractErrorMessage(
          error,
          "検収に失敗しました。時間をおいて再度お試しください。",
        ),
      );
    } finally {
      setIsCompletingInspection(false);
    }
  }, [projectId, runStatusTransition]);

  const handleRevertToInProgress = useCallback(async () => {
    if (!projectId) return;
    setWorkflowError(null);
    setIsReverting(true);
    try {
      await runStatusTransition("in_progress");
      setIsRevertConfirmOpen(false);
    } catch (error) {
      console.error("[DM] 差し戻しに失敗", error);
      setWorkflowError(
        extractErrorMessage(
          error,
          "差し戻しに失敗しました。時間をおいて再度お試しください。",
        ),
      );
    } finally {
      setIsReverting(false);
    }
  }, [projectId, runStatusTransition]);

  // 送信者によるオファーのキャンセル（contractor_pending → closed）。
  // BE 側で escrow ロック解除・キャンセル system メッセージ・受託者へのメール通知が走る。
  const handleConfirmCancel = useCallback(async () => {
    if (!projectId) return;
    setWorkflowError(null);
    setIsCancelling(true);
    try {
      await runStatusTransition("closed");
      setIsCancelConfirmOpen(false);
    } catch (error) {
      console.error("[DM] キャンセルに失敗", error);
      setWorkflowError(
        extractErrorMessage(
          error,
          "キャンセルに失敗しました。時間をおいて再度お試しください。",
        ),
      );
    } finally {
      setIsCancelling(false);
    }
  }, [projectId, runStatusTransition]);

  // 受託者によるオファーの辞退（contractor_pending → rejected）。
  // BE 側で escrow ロック解除・辞退 system メッセージが走る。
  const handleConfirmReject = useCallback(async () => {
    if (!projectId) return;
    setWorkflowError(null);
    setIsRejecting(true);
    try {
      await runStatusTransition("rejected");
      setIsRejectConfirmOpen(false);
    } catch (error) {
      console.error("[DM] 辞退に失敗", error);
      setWorkflowError(
        extractErrorMessage(
          error,
          "辞退に失敗しました。時間をおいて再度お試しください。",
        ),
      );
    } finally {
      setIsRejecting(false);
    }
  }, [projectId, runStatusTransition]);

  const handleDismissOfferModal = useCallback(() => {
    setIsOfferReceiveModalOpen(false);
  }, []);

  // ─── 契約書アイテム関連 ─────────────────────────────
  const [
    isContractReadOnlyModalOpen,
    setIsContractReadOnlyModalOpen,
  ] = useState(false);
  const handleCloseContractReadOnly = useCallback(() => {
    setIsContractReadOnlyModalOpen(false);
  }, []);

  /**
   * 契約アイテムの「詳細を確認」: 未受諾（contractor_pending）なら承認スライダー付きの
   * 受領モーダル（OfferReceiveModal）を、受諾後は読み取り専用の確認モーダルを開く。
   */
  const handleViewOfferDetail = useCallback(() => {
    if (thread.status === "contractor_pending") {
      setIsOfferReceiveModalOpen(true);
    } else {
      setIsContractReadOnlyModalOpen(true);
    }
  }, [thread.status]);

  // 「クローズを相談」は後続 PR で API 接続するため、現状はクリック検知のみ
  // （toast 等は導入していないので console.warn でログ出力）。
  const logContractStubAction = useCallback((label: string) => {
    console.warn(`[DM] 契約書「${label}」は別 PR で実装予定`);
  }, []);

  // ─── 締結後の契約更新（受託者の承認ゲート）────────────────
  // 委託者が提案 → 受託者が承認して初めて契約が変わる。提案中は
  // `thread.pending_amendment` に差分が入り、契約カードに承認 / 却下
  // （受託者）と取り下げ（委託者）を出す。
  const [isContractAmendModalOpen, setIsContractAmendModalOpen] =
    useState(false);
  const [amendmentError, setAmendmentError] = useState<string | null>(null);
  const [proposeContractAmendment, { isLoading: isProposingAmendment }] =
    useProposeContractAmendmentMutation();
  const [withdrawContractAmendment, { isLoading: isWithdrawingAmendment }] =
    useWithdrawContractAmendmentMutation();
  const [respondContractAmendment, { isLoading: isRespondingAmendment }] =
    useRespondContractAmendmentMutation();

  const pendingAmendment = thread.pending_amendment ?? null;
  const isAmendmentBusy = isWithdrawingAmendment || isRespondingAmendment;

  // 承認待ちのパネルを出す位置。最後の提案メッセージにだけ紐付ける
  // （却下 → 再提案で複数並ぶため、古い提案に出さない）。
  const latestProposalMessageId = useMemo(() => {
    const proposals = allMessages.filter(
      (m) =>
        m.type === "system" &&
        m.system_content?.kind === "contract_amendment_proposed",
    );
    return proposals.length ? proposals[proposals.length - 1].id : null;
  }, [allMessages]);

  // 提案 / 応答のいずれでも system メッセージが増えるため、成功したら
  // メッセージ一覧を取り直してタイムラインに差分カードを出す。
  const refreshAfterAmendment = useCallback(() => {
    hasInitializedMessages.current = false;
    void refetchDmMessages();
  }, [refetchDmMessages]);

  const handleProposeAmendment = useCallback(
    async (changes: ContractAmendmentDraft) => {
      if (!projectId) return;
      setAmendmentError(null);
      try {
        await proposeContractAmendment({
          project_id: projectId,
          dm_id: thread.id,
          ...changes,
        }).unwrap();
        setIsContractAmendModalOpen(false);
        refreshAfterAmendment();
      } catch (error) {
        console.error("[DM] 契約更新の提案に失敗", error);
        setAmendmentError(
          extractErrorMessage(
            error,
            "契約更新の提案に失敗しました。時間をおいて再度お試しください。",
          ),
        );
      }
    },
    [projectId, thread.id, proposeContractAmendment, refreshAfterAmendment],
  );

  const handleWithdrawAmendment = useCallback(async () => {
    if (!projectId) return;
    setAmendmentError(null);
    try {
      await withdrawContractAmendment({
        project_id: projectId,
        dm_id: thread.id,
      }).unwrap();
      refreshAfterAmendment();
    } catch (error) {
      console.error("[DM] 契約更新の取り下げに失敗", error);
      setAmendmentError(
        extractErrorMessage(error, "契約更新の取り下げに失敗しました。"),
      );
    }
  }, [projectId, thread.id, withdrawContractAmendment, refreshAfterAmendment]);

  const handleRespondAmendment = useCallback(
    async (response: "accepted" | "rejected") => {
      if (!projectId) return;
      setAmendmentError(null);
      try {
        await respondContractAmendment({
          project_id: projectId,
          dm_id: thread.id,
          response,
        }).unwrap();
        setIsAmendmentReviewOpen(false);
        refreshAfterAmendment();
      } catch (error) {
        // 増額の残高不足・受託者が原資を使い切っている減額は BE が 400 を返す。
        // 理由が UI から分からないと直しようがないので、BE のメッセージを出す。
        console.error("[DM] 契約更新への応答に失敗", error);
        setAmendmentError(
          extractErrorMessage(
            error,
            "契約更新の処理に失敗しました。時間をおいて再度お試しください。",
          ),
        );
      }
    },
    [projectId, thread.id, respondContractAmendment, refreshAfterAmendment],
  );

  // 契約更新の承認モーダル（長押し承認 / 却下）。
  // 受託者がスレッドを開いた時点で自動表示する（初回オファーの受領モーダルと
  // 同じ体験）。閉じたあとは「内容を確認」から開き直せる。
  const shouldAutoOpenAmendmentReview = isContractorViewer && !!pendingAmendment;
  const [isAmendmentReviewOpen, setIsAmendmentReviewOpen] = useState(
    shouldAutoOpenAmendmentReview,
  );

  // スレッド切替時と、提案の有無が変わったときに開閉状態を引き直す。
  // ユーザーが閉じただけでは依存値が変わらないので、勝手に開き直さない。
  useEffect(() => {
    setIsAmendmentReviewOpen(shouldAutoOpenAmendmentReview);
    setAmendmentError(null);
  }, [thread.id, shouldAutoOpenAmendmentReview]);

  const handleReviewAmendment = useCallback(() => {
    setAmendmentError(null);
    setIsAmendmentReviewOpen(true);
  }, []);

  // status_timestamps からカードの状態（pre/post/inspected/invalid）を導出
  const contractCardPhase = useMemo(
    () => getContractCardPhase(thread),
    [thread],
  );

  // 契約書 PDF プレビュー。送信/編集/引受時に BE が保存した PDF
  // （contract_files[].url）を表示し、保存が無い場合のみ動的生成にフォールバックする。
  const [contractPdfPreview, setContractPdfPreview] =
    useState<SubcontractPdfPreviewState | null>(null);

  // 契約書 PDF を表示すべきフェーズか（締結後 or 辞退）。
  const needsContractPdf =
    threadType === "subcontract" &&
    (contractCardPhase === "post" ||
      contractCardPhase === "inspected" ||
      thread.status === "rejected");

  // 保存済み契約書 PDF（送信 / 編集 / 引受のタイミングで BE が生成・保存）を
  // Detail API の contract_files から取得し、表示時の動的生成を避ける。
  const { data: contractThreadDetail } = useGetDmThreadDetailQuery(
    { project_id: projectId!, dm_id: threadId! },
    { skip: !projectId || !threadId || !needsContractPdf },
  );
  const savedContractFile = useMemo(
    () => selectLatestContractFile(contractThreadDetail?.data?.contract_files),
    [contractThreadDetail],
  );

  useEffect(() => {
    if (!projectId || !threadId || !needsContractPdf) {
      setContractPdfPreview(null);
      return;
    }
    // 保存済み PDF があればそれを表示（毎回の動的生成を避ける）。
    if (savedContractFile) {
      setContractPdfPreview({
        status: "ready",
        url: savedContractFile.url,
        filename: savedContractFile.filename,
      });
      return;
    }
    // フォールバック: 保存済みが無い（旧データ / 生成失敗）場合のみ動的生成する。
    let cancelled = false;
    setContractPdfPreview({ status: "generating" });
    requestOfferSubcontractPdf({ dmId: threadId })
      .then(({ blob, filename }) => {
        if (!cancelled) {
          setContractPdfPreview({ status: "ready", blob, filename });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setContractPdfPreview({
            status: "error",
            errorMessage: formatPdfExportError(e),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, threadId, needsContractPdf, savedContractFile]);

  // 成立後フェーズで「締結日時」として表示する時刻（受諾時刻にフォールバック）。
  const contractConcludedAt = useMemo(
    () =>
      thread.status_timestamps.contracted_at ??
      thread.status_timestamps.accepted_at ??
      null,
    [
      thread.status_timestamps.contracted_at,
      thread.status_timestamps.accepted_at,
    ],
  );

  /**
   * BE が `contract_uploaded` system メッセージを投入していない場合のフォールバック合成。
   *
   * subcontract の現行フロー（動的 PDF 生成）では、契約書ドラフトを永続化せず
   * `contract_uploaded` を投入していないケースがあるため、その場合は FE 側で
   * `status_timestamps.accepted_at`（送信遷移時刻）を起点に擬似的なアイテムを
   * 1 件だけタイムラインに合成して描画する。
   *
   * BE が将来 `contract_uploaded` を投入し始めたら、そちらが優先され重複しない。
   */
  const SYNTH_CONTRACT_ID_PREFIX = "__synth_contract_";

  const messagesToRender = useMemo<DmMessage[]>(() => {
    const hasContractUploaded = allMessages.some(
      (m) =>
        m.type === "system" &&
        m.system_content?.kind === "contract_uploaded",
    );
    if (hasContractUploaded) return allMessages;
    if (threadType !== "subcontract") return allMessages;
    // 送信直後（contractor_pending・未受諾）は accepted_at が無いため、
    // offer_summary の投入時刻（= 送信時刻）を起点に合成して、送信側でも
    // 契約アイテムが必ず表示されるようにする。
    const offerSummaryForSynth = allMessages.find(
      (m) => m.type === "system" && m.system_content?.kind === "offer_summary",
    );
    const uploadedAt =
      thread.status_timestamps.accepted_at ??
      offerSummaryForSynth?.created_at ??
      null;
    if (!uploadedAt) return allMessages;
    const isParticipant =
      !!currentUserId &&
      (thread.requester?.id === currentUserId ||
        thread.contractor?.id === currentUserId);
    if (!isParticipant) return allMessages;

    const synthAuthor: DmMessageUser | null = thread.requester
      ? {
          id: thread.requester.id,
          display_name: thread.requester.display_name,
          avatar: thread.requester.avatar_url ?? null,
          position: null,
        }
      : null;

    const synth: DmMessage = {
      id: `${SYNTH_CONTRACT_ID_PREFIX}${thread.id}`,
      message: "",
      type: "system",
      system_content: {
        kind: "contract_uploaded",
        file_id: "",
        uploaded_at: uploadedAt,
        uploaded_by: thread.requester?.id ?? 0,
        expires_at: null,
      },
      author: synthAuthor,
      parent_id: null,
      is_resolved: false,
      has_files: false,
      files: [],
      receivers: [],
      created_at: uploadedAt,
      updated_at: uploadedAt,
    };

    // `offer_summary` の直後に挿入。なければ先頭に置く。
    const idx = allMessages.findIndex(
      (m) =>
        m.type === "system" && m.system_content?.kind === "offer_summary",
    );
    if (idx >= 0) {
      return [
        ...allMessages.slice(0, idx + 1),
        synth,
        ...allMessages.slice(idx + 1),
      ];
    }
    return [synth, ...allMessages];
  }, [allMessages, threadType, thread, currentUserId]);

  // 納品物（ファイルメッセージ）に表示するテイク番号。差し戻し→再提出ごとに増える。
  // delivery_submitted system メッセージ自体は描画せず、その直前の「ファイルを持つ
  // user メッセージ」を納品物とみなしてテイク番号を対応付ける（キー = ファイルメッセージ id）。
  const deliveryTakeNumbers = useMemo<Map<string, number>>(() => {
    const map = new Map<string, number>();
    let count = 0;
    for (let i = 0; i < messagesToRender.length; i += 1) {
      const m = messagesToRender[i];
      if (
        m.type === "system" &&
        m.system_content?.kind === "delivery_submitted"
      ) {
        count += 1;
        for (let j = i - 1; j >= 0; j -= 1) {
          const prev = messagesToRender[j];
          if (
            prev.type !== "system" &&
            prev.has_files &&
            prev.files &&
            prev.files.length > 0
          ) {
            map.set(prev.id, count);
            break;
          }
        }
      }
    }
    return map;
  }, [messagesToRender]);

  /**
   * 契約書アイテムに統合表示する要素（初期メッセージ + オファーサマリ）を抽出。
   *
   * オファー送信遷移時、BE は以下 3 件をほぼ同タイミングで投入する:
   *   1. `thread.initial_message` を本文に持つ user メッセージ（送信者発）
   *   2. `offer_summary` system メッセージ
   *   3. `contract_uploaded` system メッセージ（FE 擬似合成のケースもある）
   *
   * これらをタイムライン上 1 つの「契約書アイテム」に集約するため、本フックでは
   * 1) と 2) を契約書カードへ渡す形に取り出し、map 段階で別行描画をスキップする。
   */
  const offerSummaryMsg = useMemo<DmMessage | null>(() => {
    return (
      allMessages.find(
        (m) =>
          m.type === "system" &&
          m.system_content?.kind === "offer_summary",
      ) ?? null
    );
  }, [allMessages]);

  /**
   * 初期メッセージ user message の特定。
   *
   * BE は送信遷移時に `thread.initial_message` の本文で user メッセージを自動投入するが、
   * 末尾改行・トリム・微妙な書式違いで完全一致しないケースに備えて 2 段で判定する:
   *
   *  1. 本文一致（thread.initial_message と完全一致）
   *  2. 時刻近接（`status_timestamps.accepted_at` の前後 5 分以内に投稿された
   *     送信者(requester)発の user メッセージ。送信遷移と同タイミングで投入される
   *     initial_message を狙い撃つフォールバック）
   *
   * いずれも author = requester に限定し、後続の通常メッセージを誤って隠さないようにする。
   */
  const initialMessageMsg = useMemo<DmMessage | null>(() => {
    const requesterId = thread.requester?.id;
    if (!requesterId) return null;

    const trimmedInitial = thread.initial_message?.trim();
    if (trimmedInitial) {
      const exact = allMessages.find(
        (m) =>
          m.type === "user" &&
          m.author?.id === requesterId &&
          (m.message ?? "").trim() === trimmedInitial,
      );
      if (exact) return exact;
    }

    const acceptedAt = thread.status_timestamps.accepted_at;
    if (acceptedAt) {
      const acceptedTime = new Date(acceptedAt).getTime();
      const NEAR_WINDOW_MS = 5 * 60 * 1000;
      const nearby = allMessages.find(
        (m) =>
          m.type === "user" &&
          m.author?.id === requesterId &&
          Math.abs(new Date(m.created_at).getTime() - acceptedTime) <
            NEAR_WINDOW_MS,
      );
      if (nearby) return nearby;
    }

    return null;
  }, [
    allMessages,
    thread.initial_message,
    thread.requester?.id,
    thread.status_timestamps.accepted_at,
  ]);

  // 契約書アイテム内に統合表示するため、タイムラインの map では別行で描画しない ID 集合。
  const mergedIntoContractIds = useMemo<Set<string>>(() => {
    const set = new Set<string>();
    if (offerSummaryMsg) set.add(offerSummaryMsg.id);
    if (initialMessageMsg) set.add(initialMessageMsg.id);
    return set;
  }, [offerSummaryMsg, initialMessageMsg]);

  // 契約書アイテムに渡す納期 / 対価は `contractVersions` がアイテムごとに
  // 解決する（締結後に契約が更新されても各アイテムはその時点の値を保つ）。
  // 担当ロール / スコープ / 成果物有無はスレッド一覧・上部に既に表示されている
  // ため、本アイテムでは扱わない。

  // 契約書カードに渡す初期メッセージ。編集で更新されうるため thread の値を優先し、
  // 無ければ送信時に投入された user メッセージにフォールバックする。
  const contractInitialMessage = useMemo(() => {
    return thread.initial_message ?? initialMessageMsg?.message ?? null;
  }, [thread.initial_message, initialMessageMsg]);

  /**
   * タイムラインの契約書アイテムを「その時点の版」に固定するための解決結果。
   *
   * 契約が更新されても、初回の契約書アイテムは **提出時のまま**（当時の内容と
   * 当時の PDF）を表示し続け、更新アイテムはそれぞれの更新後の内容を表示する。
   * スレッドが持つのは常に最新値だけなので、更新メッセージの情報から逆算する。
   *
   * - **PDF**: BE は各更新メッセージに *更新前* の PDF を添付する。つまりある
   *   時点の契約書は「次の更新に添付された PDF」であり、次が無ければ現行の
   *   `contract_file`（= `contractPdfPreview`）。
   * - **内容**: `changes[field].previous` を新しい順に辿って巻き戻す
   *   （`resolveContractSnapshot`）。
   */
  const contractVersions = useMemo(() => {
    const amendedMessages = allMessages.filter(
      (m) =>
        m.type === "system" && m.system_content?.kind === "contract_amended",
    );
    const changesList = amendedMessages.map((m) =>
      m.system_content?.kind === "contract_amended"
        ? m.system_content.changes
        : {},
    );
    const currentSnapshot = {
      deadline: thread.deadline ?? null,
      contract_amount: thread.contract_amount ?? null,
      initial_message: contractInitialMessage,
    };

    // 各時点の PDF = 次の更新に添付された「更新前の PDF」。無ければ現行。
    const pdfAfter = (index: number): SubcontractPdfPreviewState | null => {
      const archived = amendedMessages[index + 1]?.files?.[0];
      const url = archived?.download_url || archived?.original_url;
      if (!url) return contractPdfPreview;
      return { status: "ready", url, filename: archived?.file_name ?? null };
    };

    // 現在有効な版は最後の 1 つだけ。それ以前は更新によって無効になっている。
    const lastIndex = amendedMessages.length - 1;

    return {
      // 初回の契約書アイテム（= すべての更新より前）
      initial: {
        snapshot: resolveContractSnapshot(changesList, currentSnapshot),
        pdf: pdfAfter(-1),
        isActive: amendedMessages.length === 0,
      },
      // 各更新アイテム（= その更新の直後）。要点の一覧は初回アイテムだけが持ち、
      // 更新アイテムは変更点と契約書 PDF を出すので snapshot は要らない。
      byAmendmentId: new Map(
        amendedMessages.map((msg, index) => [
          msg.id,
          { pdf: pdfAfter(index), isActive: index === lastIndex },
        ]),
      ),
    };
  }, [
    allMessages,
    contractPdfPreview,
    thread.deadline,
    thread.contract_amount,
    contractInitialMessage,
  ]);

  // 既読機能用の ref
  // - pendingReadMessageIds: 既読 API 送信待ちキュー
  // - readDebounceTimer: debounce / retry 共用タイマー
  // - messageElementRefs: IntersectionObserver で監視対象の DOM 要素
  // - intersectionObserverRef: 画面内判定用 Observer
  // - hasBulkReadInitializedRef: スレッド開封直後に 1 回だけ一括既読を走らせるためのフラグ
  const pendingReadMessageIds = useRef<Set<string>>(new Set());
  const readDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageElementRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const hasBulkReadInitializedRef = useRef(false);

  // allMessages の最新値を参照する ref（ハンドラーの stale closure を避ける）
  const allMessagesRef = useRef<DmMessage[]>([]);
  allMessagesRef.current = allMessages;

  // メッセージ送信/編集/返信/削除用 state
  const [messageToEdit, setMessageToEdit] = useState<DmMessage | null>(null);
  const [messageToReply, setMessageToReply] = useState<DmMessage | null>(null);
  const [messageToDelete, setMessageToDelete] = useState<DmMessage | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ファイルアップロード関連 state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [selectedFilesWithPath, setSelectedFilesWithPath] = useState<FileWithPath[]>([]);
  const [editExistingFiles, setEditExistingFiles] = useState<ExistingAttachedFile[]>([]);
  const [editFilesChanged, setEditFilesChanged] = useState(false);

  // スレッド切替時にローカル state をリセット
  useEffect(() => {
    hasInitializedMessages.current = false;
    didInitialScrollRef.current = false;
    setAllMessages([]);
    setMessageToEdit(null);
    setMessageToReply(null);
    setMessageToDelete(null);
    setSelectedFiles([]);
    setSelectedFilesWithPath([]);
    setEditExistingFiles([]);
    setEditFilesChanged(false);
    setIsContractReadOnlyModalOpen(false);

    // 既読関連の状態（キュー / Observer / 要素 ref / 一括既読フラグ）もリセット
    pendingReadMessageIds.current.clear();
    messageElementRefs.current.clear();
    hasBulkReadInitializedRef.current = false;
    if (readDebounceTimer.current) {
      clearTimeout(readDebounceTimer.current);
      readDebounceTimer.current = null;
    }
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
  }, [threadId]);

  // サーバ取得メッセージをローカル state に反映
  useEffect(() => {
    const msgs = messagesData?.data?.messages;
    if (msgs && !hasInitializedMessages.current) {
      hasInitializedMessages.current = true;
      setAllMessages(sortMessagesByDate(msgs));
    }
  }, [messagesData]);

  // 編集・削除のカットオフ（status_timestamps の最大日時）
  const editCutoff = useMemo(
    () => getDmEditCutoff(thread.status_timestamps),
    [thread.status_timestamps]
  );

  const isOwnSideMessage = useCallback(
    (msg: DmMessage): boolean => {
      if (!msg.author) return false;

      if (threadType === "subcontract") {
        return msg.author.id === currentUserId;
      }

      if (!msg.author.position) return false;
      const authorPosition = msg.author.position;
      const userPosition = membershipData?.data?.position;
      if (userPosition && isProducerGroupPosition(userPosition)) {
        return isProducerGroupPosition(authorPosition);
      }
      if (userPosition && isClientGroupPosition(userPosition)) {
        return isClientGroupPosition(authorPosition);
      }
      return false;
    },
    [membershipData, threadType, currentUserId]
  );

  // 既読登録後の楽観更新用の自ユーザー情報
  const currentUserForReceivers = useMemo((): DmMessageUser | null => {
    if (!currentUserData?.data) return null;
    const user = currentUserData.data;
    return {
      id: user.id,
      display_name: user.stage_name || user.fullname || "Unknown",
      avatar: user.icon || null,
      position: (membershipData?.data?.position as DmMessageUser["position"]) ?? null,
    };
  }, [currentUserData, membershipData]);

  // user_id → position のマップ（receiver に position が無い場合のフォールバック）
  const memberPositionMap = useMemo(() => {
    const map = new Map<number, string>();
    allMembershipsData?.data?.forEach((m: ProjectMembership) => {
      if (m.user_id && m.position) {
        map.set(m.user_id, m.position.toUpperCase());
      }
    });
    return map;
  }, [allMembershipsData]);

  /**
   * 対象メッセージが未読かどうか判定する。
   *
   * BE 仕様:
   * - `DmMessage.receivers` は「そのメッセージの受信者（スレッド参加者）リスト」。
   *   各 receiver の `seen_at` に実際に既読化した時刻が入る（未読なら null）。
   *
   * 判定:
   * - システム / 自メッセージ / author 不明は対象外
   * - `subcontract` および master_contract でグループ未確定（SPECIALIST 等）:
   *   自分エントリの `seen_at` が未設定なら未読
   * - `master_contract` でグループ所属あり（CLIENT/REVIEWER 系 or PRODUCER/DIRECTOR/ADMIN 系）:
   *   同じグループの誰か1人でも `seen_at` を持っていれば既読扱い
   */
  const isMessageUnread = useCallback(
    (msg: DmMessage): boolean => {
      if (!currentUserId) return false;
      if (msg.type !== "user") return false;
      if (!msg.author || msg.author.id === currentUserId) return false;

      const isSeenByMe = (): boolean => {
        const me = msg.receivers.find((r) => r.id === currentUserId);
        return !!me?.seen_at;
      };

      if (threadType === "subcontract") {
        return !isSeenByMe();
      }

      const userPosition = membershipData?.data?.position;
      if (!userPosition) return false;

      let isMyGroup: ((position: string) => boolean) | null = null;
      if (isClientGroupPosition(userPosition)) {
        isMyGroup = isClientGroupPosition;
      } else if (isProducerGroupPosition(userPosition)) {
        isMyGroup = isProducerGroupPosition;
      }
      if (!isMyGroup) {
        // グループ所属のない role は自分自身の seen_at で判定
        return !isSeenByMe();
      }

      const readByMyGroup = msg.receivers.some((r) => {
        if (!r.seen_at) return false;
        const pos = r.position || memberPositionMap.get(r.id) || "";
        return !!pos && isMyGroup!(pos);
      });
      return !readByMyGroup;
    },
    [currentUserId, threadType, membershipData, memberPositionMap],
  );

  // 既読 API 呼び出し本体（debounce 後 or 開封時一括で呼ばれる）
  const processReadMessages = useCallback(async () => {
    if (!projectId || !threadId) return;
    if (pendingReadMessageIds.current.size === 0) return;
    if (!currentUserForReceivers) return;

    const messageIds = Array.from(pendingReadMessageIds.current);
    pendingReadMessageIds.current.clear();
    const messageIdSet = new Set(messageIds);
    const selfId = currentUserForReceivers.id;

    try {
      await markDmMessagesAsRead({
        project_id: projectId,
        dm_id: threadId,
        message_ids: messageIds,
      }).unwrap();

      // 楽観更新: receivers 内の自分エントリの seen_at を更新
      // （BE 仕様上、スレッド参加者は最初から receivers に含まれる）
      const now = new Date().toISOString();
      setAllMessages((prev) =>
        prev.map((msg) => {
          if (!messageIdSet.has(msg.id)) return msg;
          const idx = msg.receivers.findIndex((r) => r.id === selfId);
          if (idx < 0 || msg.receivers[idx].seen_at) return msg;
          const newReceivers = [...msg.receivers];
          newReceivers[idx] = { ...newReceivers[idx], seen_at: now };
          return { ...msg, receivers: newReceivers };
        }),
      );
    } catch (error) {
      console.warn("[DM processReadMessages] 既読登録エラー、リトライ予定:", error);
      // 失敗分をキューに戻し、一定時間後にリトライ
      messageIds.forEach((id) => pendingReadMessageIds.current.add(id));
      if (readDebounceTimer.current) clearTimeout(readDebounceTimer.current);
      readDebounceTimer.current = setTimeout(() => {
        processReadMessages();
      }, READ_RETRY_MS);
    }
  }, [projectId, threadId, currentUserForReceivers, markDmMessagesAsRead]);

  // IntersectionObserver 経由で既読キューに追加（debounce で束ねる）
  const addToReadQueue = useCallback(
    (messageId: string) => {
      pendingReadMessageIds.current.add(messageId);
      if (readDebounceTimer.current) clearTimeout(readDebounceTimer.current);
      readDebounceTimer.current = setTimeout(() => {
        processReadMessages();
      }, READ_DEBOUNCE_MS);
    },
    [processReadMessages],
  );

  // スレッド開封時: ロード済みの未読メッセージを一括で既読化（debounce せず即時送信）
  useEffect(() => {
    if (hasBulkReadInitializedRef.current) return;
    if (!currentUserId || !currentUserForReceivers) return;
    if (allMessages.length === 0) return;

    const unreadIds = allMessages
      .filter((msg) => isMessageUnread(msg))
      .map((msg) => msg.id);

    hasBulkReadInitializedRef.current = true;
    if (unreadIds.length === 0) return;

    unreadIds.forEach((id) => pendingReadMessageIds.current.add(id));
    if (readDebounceTimer.current) clearTimeout(readDebounceTimer.current);
    processReadMessages();
  }, [
    allMessages,
    currentUserId,
    currentUserForReceivers,
    isMessageUnread,
    processReadMessages,
  ]);

  // IntersectionObserver セットアップ（スクロール・追加ロード時の既読化）
  useEffect(() => {
    if (!currentUserId) return;
    if (allMessages.length === 0) return;

    intersectionObserverRef.current?.disconnect();
    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const messageId = entry.target.getAttribute("data-message-id");
          if (!messageId) return;
          const msg = allMessagesRef.current.find((m) => m.id === messageId);
          if (msg && isMessageUnread(msg)) {
            addToReadQueue(messageId);
          }
        });
      },
      {
        root: contentRef.current,
        rootMargin: "0px",
        threshold: READ_VISIBILITY_THRESHOLD,
      },
    );

    messageElementRefs.current.forEach((element) => {
      intersectionObserverRef.current?.observe(element);
    });

    return () => {
      intersectionObserverRef.current?.disconnect();
    };
  }, [allMessages, currentUserId, isMessageUnread, addToReadQueue]);

  // メッセージ要素の ref を登録/解除するコールバック
  const registerMessageRef = useCallback(
    (messageId: string, element: HTMLDivElement | null) => {
      if (element) {
        messageElementRefs.current.set(messageId, element);
        intersectionObserverRef.current?.observe(element);
      } else {
        const existing = messageElementRefs.current.get(messageId);
        if (existing && intersectionObserverRef.current) {
          intersectionObserverRef.current.unobserve(existing);
        }
        messageElementRefs.current.delete(messageId);
      }
    },
    [],
  );

  // ダウンロード既読の API 送信（一括ダウンロード時の連続呼び出しを配列 1 回の PATCH にまとめる）
  const enqueueMarkDownloaded = useBatchedCall<string>(async (fileIds) => {
    if (!projectId || !threadId) return;
    try {
      await markDmFilesAsDownloaded({
        project_id: projectId,
        dm_id: threadId,
        downloaded_file_ids: fileIds,
      }).unwrap();
    } catch (error) {
      console.warn("[DM handleFileDownload] DL 既読登録エラー:", error);
    }
  });

  // ファイルダウンロード記録（楽観更新: UI即反映 → API送信）
  const handleFileDownload = useCallback(
    (messageId: string, fileId: string) => {
      if (!projectId || !threadId) return;
      if (!currentUserForReceivers) return;

      const messages = allMessagesRef.current;
      const msg = messages.find((m) => m.id === messageId);
      const targetFile = msg?.files.find((f) => f.id === fileId);
      if (
        targetFile?.downloaded_users?.some((u) => u.id === currentUserForReceivers.id)
      ) {
        return;
      }

      // receivers 側に自分のアバターが入っていればそちらを優先
      const receiverSelf = msg?.receivers.find(
        (r) => r.id === currentUserForReceivers.id,
      );
      const avatarUrl = receiverSelf?.avatar ?? currentUserForReceivers.avatar;

      setAllMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          return {
            ...m,
            files: m.files.map((file) => {
              if (file.id !== fileId) return file;
              return {
                ...file,
                downloaded_users: [
                  ...(file.downloaded_users || []),
                  {
                    id: currentUserForReceivers.id,
                    display_name: currentUserForReceivers.display_name,
                    avatar: avatarUrl,
                    downloaded_at: new Date().toISOString(),
                  },
                ],
              };
            }),
          };
        }),
      );

      enqueueMarkDownloaded(fileId);
    },
    [projectId, threadId, currentUserForReceivers, enqueueMarkDownloaded],
  );

  // 最下部スクロール
  const scrollToBottom = useCallback(() => {
    if (!contentRef.current) return;
    contentRef.current.scrollTop = contentRef.current.scrollHeight;
  }, []);

  const dispatch = useDispatch();

  // 内側スクロール（MessengerContent）→ state.layout への連動。
  // GlobalWrapper の window レベル検出は SP で取りこぼしやすいため、対象コンテナに
  // 直接ハンドラを貼って BottomAppBar / DmActionPanel の表示位置と確実に連動させる
  //（詳細は useScrollDirectionLayoutSync を参照）。
  const {
    onWheel: handleWheel,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
  } = useScrollDirectionLayoutSync();

  // 初回ロード・スレッド切替時に最下部へスクロール。
  // - isLoadingMessages が false になった時点では allMessages はまだ空のことがあるため
  //   （messagesData → setAllMessages は別 effect で行われる）、ここでは allMessages の
  //   描画完了を待ってから scrollTop を設定する。
  // - useLayoutEffect でペイント前に確定させ、「一瞬上から見えてスクロールする」挙動を回避。
  // - スレッドごとに一度だけ行うため didInitialScrollRef でガード。
  // - 画像/サムネの遅延ロードで scrollHeight が伸びるケースに備え、数フレーム分再適用する。
  useLayoutEffect(() => {
    if (isLoadingMessages) return;
    if (allMessages.length === 0) return;
    if (didInitialScrollRef.current) return;
    didInitialScrollRef.current = true;

    scrollToBottom();

    // 画像などの遅延サイズ確定による scrollHeight 変化に追従するため、
    // 短時間だけ rAF で再スクロールする（ユーザー操作を妨げない軽量な方式）。
    let frameCount = 0;
    let rafId = 0;
    const tick = () => {
      scrollToBottom();
      frameCount += 1;
      if (frameCount < 10) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isLoadingMessages, allMessages.length, scrollToBottom]);

  // アップロード完了時にモーダルを自動で閉じる。
  // エラー行が残っている場合は自動クリアしない（再試行ボタン付きで表示し続け、
  // 失敗ダイアログの選択で解決する）
  useEffect(() => {
    if (uploadingFiles.length === 0) return;
    const allCompleted = uploadingFiles.every((f) => f.status === "completed");
    if (allCompleted) {
      const timer = setTimeout(() => {
        clearCompleted();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [uploadingFiles, clearCompleted]);

  // ファイル選択ハンドラ
  const handleFilesSelected = useCallback((files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveFile = useCallback(
    (index: number) => {
      const normalFilesCount = selectedFiles.length;
      if (index < normalFilesCount) {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      } else {
        const folderIndex = index - normalFilesCount;
        setSelectedFilesWithPath((prev) => prev.filter((_, i) => i !== folderIndex));
      }
    },
    [selectedFiles.length]
  );

  const handleRemoveExistingFile = useCallback((fileId: string) => {
    setEditExistingFiles((prev) => prev.filter((f) => f.id !== fileId));
    setEditFilesChanged(true);
  }, []);

  const handleFolderDrop = useCallback((filesWithPath: FileWithPath[]) => {
    if (filesWithPath.length === 0) return;
    setSelectedFilesWithPath((prev) => [...prev, ...filesWithPath]);
  }, []);

  // モード解除ハンドラ（handleSendMessage 内でも参照するため先に定義）
  const cancelEdit = useCallback(() => {
    setMessageToEdit(null);
    setEditExistingFiles([]);
    setEditFilesChanged(false);
  }, []);
  const cancelReply = useCallback(() => setMessageToReply(null), []);
  const cancelDelete = useCallback(() => setMessageToDelete(null), []);

  // 本文中の [MM:SS] クリックで、直近の video/audio 添付にシーク再生する
  const {
    handleMediaRef,
    handlePlayStateChange,
    handleTimestampClick,
  } = useTimestampSeek(allMessages);

  // CommentField 用の全選択ファイル
  const allSelectedFiles = useMemo(
    () => [...selectedFiles, ...selectedFilesWithPath.map((f) => f.file)],
    [selectedFiles, selectedFilesWithPath]
  );

  /**
   * 現在選択中のファイル（通常選択 + フォルダ選択）を S3 にアップロードし、
   * 続行してよい file_key を返す。失敗があれば「再試行 / 失敗分を除いて続行 /
   * キャンセル」ダイアログで解決し、キャンセル時は選択状態を復元して null を返す。
   */
  const uploadPendingFiles = useCallback(async (): Promise<string[] | null> => {
    if (selectedFiles.length === 0 && selectedFilesWithPath.length === 0) return [];
    const normalFilesWithPath: FileWithPath[] = selectedFiles.map((file) => ({
      file,
      relativePath: file.name,
    }));
    const allFilesToUpload: FileWithPath[] = [
      ...normalFilesWithPath,
      ...selectedFilesWithPath,
    ];
    setSelectedFiles([]);
    setSelectedFilesWithPath([]);

    const uploadResults = await uploadFiles(allFilesToUpload);
    const hadFailures = uploadResults.some((f) => f.status === "error");
    const resolved = await resolveUploadFailures(uploadResults);
    if (resolved === null) {
      // キャンセル: 進捗を畳み、添付を選択状態へ戻して中断
      clearCompleted();
      setSelectedFiles(selectedFiles);
      setSelectedFilesWithPath(selectedFilesWithPath);
      return null;
    }
    if (hadFailures) clearCompleted();
    return resolved
      .filter((f) => f.fileKey)
      .map((f) => f.fileKey as string);
  }, [selectedFiles, selectedFilesWithPath, uploadFiles, resolveUploadFailures, clearCompleted]);

  /**
   * 編集対象メッセージに対し、UI 上で残された既存ファイルと新規アップロードファイルから
   * BE に送る file_keys を組み立てる。添付の変更が無ければ undefined を返し、PATCH 時に
   * file_keys を送らない（BE 側では未指定 = 変更なし扱い）。
   */
  const composeEditFileKeys = useCallback(
    (target: DmMessage, newFileKeys: string[]): string[] | undefined => {
      if (!editFilesChanged && newFileKeys.length === 0) return undefined;
      const keptFiles = target.files.filter((f) =>
        editExistingFiles.some((ef) => ef.id === f.id)
      );
      const existingFileKeys = keptFiles
        .map((f) => f.file_key || extractS3KeyFromUrl(f.original_url || ""))
        .filter((k): k is string => !!k);
      return [...existingFileKeys, ...newFileKeys];
    },
    [editFilesChanged, editExistingFiles]
  );

  // メッセージ送信・編集ハンドラ
  const handleSendMessage = useCallback(
    async (messageText: string, meta?: AiAssistSendMeta) => {
      if (!projectId || !threadId) return;
      if (isSending || isUploading) return;

      const trimmed = messageText.trim();
      const hasMessage = trimmed !== "";
      const hasPendingFiles =
        selectedFiles.length > 0 || selectedFilesWithPath.length > 0;

      // 何も送れるものが無い場合（本文なし・新規ファイルなし・編集時の既存残置もなし）は no-op
      if (!hasMessage && !hasPendingFiles && editExistingFiles.length === 0) return;

      // ─── 編集モード ───
      if (messageToEdit) {
        try {
          const newFileKeys = hasPendingFiles ? await uploadPendingFiles() : [];
          // アップロード失敗ダイアログでキャンセル: 更新を中断（本文は復元される）
          if (newFileKeys === null) return false;
          const fileKeysToSend = composeEditFileKeys(messageToEdit, newFileKeys);

          const doUpdate = () => updateMessage({
            project_id: projectId,
            dm_id: threadId,
            message_id: messageToEdit.id,
            message: trimmed,
            file_keys: fileKeysToSend,
            ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
            ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
            ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
          }).unwrap();
          // 添付をアップロードした場合、反映が終わるまでは離脱を引き止める
          const result = newFileKeys.length > 0
            ? await withPostProcess(doUpdate, "メッセージを更新中")
            : await doUpdate();

          if (result.data?.message) {
            const updated = result.data.message;
            setAllMessages((prev) =>
              prev.map((m) => (m.id === updated.id ? updated : m))
            );
          }
          cancelEdit();
        } catch (error) {
          console.error("[DM handleSendMessage] メッセージ更新エラー:", error);
        }
        return;
      }

      // ─── 通常送信 ───
      let fileKeys: string[] = [];
      if (hasPendingFiles) {
        try {
          const uploaded = await uploadPendingFiles();
          // アップロード失敗ダイアログでキャンセル: 送信を中断（本文は復元される）
          if (uploaded === null) return false;
          fileKeys = uploaded;
          if (fileKeys.length === 0 && !hasMessage) {
            console.warn(
              "[DM handleSendMessage] アップロード失敗、メッセージ本文もないため送信スキップ"
            );
            return;
          }
        } catch (error) {
          console.error("[DM handleSendMessage] ファイルアップロードエラー:", error);
          if (!hasMessage) return;
        }
      }

      try {
        const doSend = () => sendMessage({
          project_id: projectId,
          dm_id: threadId,
          message: trimmed,
          parent_id: messageToReply?.id || null,
          file_keys: fileKeys.length > 0 ? fileKeys : undefined,
          ...(meta?.original_comment !== undefined && { original_comment: meta.original_comment }),
          ...(meta?.ai_comment !== undefined && { ai_comment: meta.ai_comment }),
          ...(meta?.active_variant !== undefined && { active_variant: meta.active_variant }),
        }).unwrap();
        // 添付をアップロードした場合、送信が終わるまでは離脱を引き止める
        const result = fileKeys.length > 0
          ? await withPostProcess(doSend, "メッセージを送信中")
          : await doSend();

        setMessageToReply(null);

        if (result.data?.message) {
          const newMessage = result.data.message;
          setAllMessages((prev) =>
            prev.some((m) => m.id === newMessage.id) ? prev : [...prev, newMessage]
          );
        }

        setTimeout(() => scrollToBottom(), 100);
      } catch (error) {
        console.error("[DM handleSendMessage] メッセージ送信エラー:", error);
      }
    },
    [
      projectId,
      threadId,
      isSending,
      isUploading,
      messageToEdit,
      messageToReply,
      selectedFiles.length,
      selectedFilesWithPath.length,
      editExistingFiles.length,
      uploadPendingFiles,
      composeEditFileKeys,
      sendMessage,
      updateMessage,
      withPostProcess,
      cancelEdit,
      scrollToBottom,
    ]
  );

  // 返信ハンドラ
  const handleReply = useCallback(
    (message: MessageData) => {
      const dm = allMessages.find((m) => m.id === message.id);
      if (dm) setMessageToReply(dm);
    },
    [allMessages]
  );

  // 編集ハンドラ
  const handleEdit = useCallback(
    (message: MessageData) => {
      const dm = allMessages.find((m) => m.id === message.id);
      if (!dm) return;
      setMessageToEdit(dm);
      setEditExistingFiles(
        dm.files.map((f) => ({
          id: f.id,
          fileName: f.file_name ?? "",
          fileType: f.file_type ?? "",
        }))
      );
      setEditFilesChanged(false);
    },
    [allMessages]
  );

  // 削除ハンドラ（確認モーダル表示）
  const handleDelete = useCallback(
    (message: MessageData) => {
      const dm = allMessages.find((m) => m.id === message.id);
      if (dm) setMessageToDelete(dm);
    },
    [allMessages]
  );

  // 削除確定処理
  const confirmDelete = useCallback(async () => {
    if (!messageToDelete || !projectId || !threadId || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteMessage({
        project_id: projectId,
        dm_id: threadId,
        message_id: messageToDelete.id,
      }).unwrap();
      setAllMessages((prev) => prev.filter((msg) => msg.id !== messageToDelete.id));
      setMessageToDelete(null);
    } catch (error) {
      console.error("[DM confirmDelete] メッセージ削除エラー:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [messageToDelete, projectId, threadId, isDeleting, deleteMessage]);

  // SPECIALIST は BottomAppBar 非表示（DM のみアクセス可）のため、
  // ActionPanel をスクロールに連動させず常に viewport 下端で固定する。
  const isSpecialist = isSpecialistPosition(membershipData?.data?.position ?? "");

  // GlobalAudioToggle と同方式: BottomAppBar 表示時は 80px 上げる。
  // ただし SPECIALIST は BAB がないため常に 0 で固定（スクロール非連動）。
  const actionPanelBottom = isSpecialist
    ? 0
    : scrollState !== "down"
      ? BOTTOM_BAR_HEIGHT
      : 0;

  // メッセージエリアの下端が DmActionPanel / BottomAppBar で隠れないよう、
  // CommentField の実高さ + パネルのビューポート下端からのオフセット + 余白 を
  // padding-bottom に確保する。
  const messengerBottomPadding = Math.max(
    MESSENGER_BOTTOM_PADDING_MIN,
    commentFieldHeight + actionPanelBottom + MESSENGER_BOTTOM_PADDING_BUFFER,
  );

  // CommentField に渡す返信対象（DmMessage → ReplyToMessage 互換に整形）
  const replyToMessageForField = useMemo(() => {
    if (!messageToReply || !messageToReply.author) return null;
    return {
      id: messageToReply.id,
      author: { display_name: messageToReply.author.display_name },
      message: messageToReply.message,
    };
  }, [messageToReply]);

  // ─── WebSocket リアルタイム連携 ─────────────────────
  //
  // 他ユーザーの DM 送信/編集/削除/既読/ダウンロード既読を `/ws/spa/events` 経由で受信し、
  // allMessages を差分更新する。`type_offer` は dm_id ベースで特定するので FE では参照しない。
  const { sendEvent } = useMessengerSocket();

  // WS 受信ハンドラから reconnect フォールバックを呼ぶための ref。
  const handleReconnectRef = useRef<(() => void) | null>(null);

  const handleWsEvent = useCallback(
    (ev: MessengerIncomingEvent) => {
      if (!projectId || !threadId) return;
      switch (ev.type) {
        case "message.dm.created":
        case "message.dm.updated": {
          const { message_id } = ev.payload;
          fetchDmMessageDetail(
            { project_id: projectId, dm_id: threadId, message_id },
            false,
          )
            .unwrap()
            .then((res) => {
              const fetched = extractDetailEntity<DmMessage>(res);
              if (!fetched) {
                console.warn(
                  "[DM:WS] detail response shape unexpected",
                  res,
                );
                handleReconnectRef.current?.();
                return;
              }
              setAllMessages((prev) => {
                const exists = prev.some((m) => m.id === fetched.id);
                if (exists) {
                  return prev.map((m) => (m.id === fetched.id ? fetched : m));
                }
                return sortMessagesByDate([...prev, fetched]);
              });
            })
            .catch((err) => {
              console.warn(
                "[DM:WS] fetchDmMessageDetail failed, falling back to list refetch",
                err,
              );
              handleReconnectRef.current?.();
            });
          break;
        }
        case "message.dm.deleted": {
          const { message_id } = ev.payload;
          setAllMessages((prev) => prev.filter((m) => m.id !== message_id));
          break;
        }
        case "message.dm.read": {
          const { message_ids, reader_user_id } = ev.payload;
          if (!message_ids?.length) break;
          const idSet = new Set(message_ids);
          setAllMessages((prev) =>
            prev.map((msg) => {
              if (!idSet.has(msg.id)) return msg;
              if (msg.receivers.some((r) => r.id === reader_user_id)) return msg;
              const knownAuthor =
                msg.author?.id === reader_user_id ? msg.author : undefined;
              const knownReceiver = msg.receivers.find(
                (r) => r.id === reader_user_id,
              );
              const user: DmMessageUser = knownReceiver ?? knownAuthor ?? {
                id: reader_user_id,
                display_name: "",
                avatar: null,
                position: null,
              };
              return {
                ...msg,
                receivers: [
                  ...msg.receivers,
                  { ...user, seen_at: new Date().toISOString() },
                ],
              };
            }),
          );
          break;
        }
        case "message.dm.download_read": {
          const { message_ids, file_ids, reader_user_id } = ev.payload;
          if (!message_ids?.length || !file_ids?.length) break;
          const mIdSet = new Set(message_ids);
          const fIdSet = new Set(file_ids);
          setAllMessages((prev) =>
            prev.map((msg) => {
              if (!mIdSet.has(msg.id)) return msg;
              return {
                ...msg,
                files: msg.files.map((file) => {
                  if (!fIdSet.has(file.id)) return file;
                  if (file.downloaded_users?.some((u) => u.id === reader_user_id))
                    return file;
                  const knownReceiver = msg.receivers.find(
                    (r) => r.id === reader_user_id,
                  );
                  return {
                    ...file,
                    downloaded_users: [
                      ...(file.downloaded_users || []),
                      {
                        id: reader_user_id,
                        display_name: knownReceiver?.display_name ?? "",
                        avatar: knownReceiver?.avatar ?? null,
                        downloaded_at: new Date().toISOString(),
                      },
                    ],
                  };
                }),
              };
            }),
          );
          break;
        }
        case "dm.thread.updated": {
          // オファーの状態遷移（承諾 / 辞退 / 締結 / 納品 / 検収 等）。操作した
          // 相手側にのみ届く（操作者本人は exclude_user_id で除外）。
          // ステータス・締結状態を反映するためスレッドキャッシュを無効化し、
          // 状態遷移で投入された system / user メッセージを一覧再取得で取り込む。
          dispatch(
            dmThreadsApi.util.invalidateTags([
              "DmThreads",
              { type: "DmThreadDetail", id: threadId },
            ]),
          );
          handleReconnectRef.current?.();
          break;
        }
        default:
          break;
      }
    },
    [projectId, threadId, fetchDmMessageDetail, dispatch],
  );

  useMessengerSubscribe("dm", threadId, handleWsEvent);

  // allMessages 内の author / receivers / downloaded_users を Redux userDirectory に
  // 集約。加えてスレッドの閲覧メンバー（allMembershipsData）も流し込む。これにより
  // WS read / download_read で未知ユーザーが来てもアイコンを解決できる。
  const directoryUsers = useMemo<UserDirectoryUpsert[]>(() => {
    const fromMessages = collectMessengerUsers(allMessages);
    const fromMemberships: UserDirectoryUpsert[] = (allMembershipsData?.data ?? [])
      .filter((mbr) => typeof mbr.user_id === "number")
      .map((mbr) => ({
        id: mbr.user_id,
        display_name: mbr.display_name_jp || mbr.display_name_en || undefined,
        avatar: mbr.avatar_url ?? null,
      }));
    return [...fromMessages, ...fromMemberships];
  }, [allMessages, allMembershipsData]);
  useSyncUserDirectory(directoryUsers);

  const handleReconnect = useCallback(() => {
    if (!projectId || !threadId) return;
    fetchDmMessages(
      { project_id: projectId, dm_id: threadId, filter: "all" },
      false,
    )
      .unwrap()
      .then((result) => {
        if (!result?.data?.messages) return;
        setAllMessages(sortMessagesByDate(result.data.messages));
      })
      .catch((err) => {
        console.warn("[DM:WS] reconnect refetch failed", err);
      });
  }, [projectId, threadId, fetchDmMessages]);

  handleReconnectRef.current = handleReconnect;

  useMessengerReconnect(handleReconnect);

  // Typing 送信（scope: dm）。スレッド切替時は useTypingSender 内部で state がリセットされる。
  const { onTypingChange, onTypingCommit } = useTypingSender({
    send: sendEvent,
    scope: "dm",
    ids: {
      project_id: projectId || "",
      dm_id: threadId || "",
      parent_id: messageToReply?.id ?? null,
    },
    enabled: Boolean(projectId && threadId),
  });

  const allMessagesRefForName = useRef<DmMessage[]>([]);
  allMessagesRefForName.current = allMessages;

  const resolveTypingUserName = useCallback(
    (userId: number): string | undefined => {
      if (currentUserId === userId && currentUserForReceivers) {
        return currentUserForReceivers.display_name;
      }
      const fromMsg = allMessagesRefForName.current.find(
        (m) => m.author?.id === userId,
      )?.author;
      if (fromMsg?.display_name) return fromMsg.display_name;
      const fromReceiver = allMessagesRefForName.current
        .flatMap((m) => m.receivers)
        .find((r) => r.id === userId);
      if (fromReceiver?.display_name) return fromReceiver.display_name;
      // スレッドメンバーリスト（all memberships）にフォールバック。
      const fromMembership = allMembershipsData?.data?.find(
        (m: ProjectMembership) => m.user_id === userId,
      );
      return (
        fromMembership?.display_name_jp ||
        fromMembership?.display_name_en ||
        undefined
      );
    },
    [currentUserId, currentUserForReceivers, allMembershipsData],
  );

  return (
    <MessengerContainer ref={containerRef}>
      <DropZoneOverlay
        enabled={!isLoadingMessages}
        onDrop={handleFilesSelected}
        overlayText="ファイルまたはフォルダをドロップしてアップロード"
        dragActiveText="ここにドロップ"
        enableFolderDrop={true}
        onDropWithPath={handleFolderDrop}
      >
        <MessengerContent
          ref={contentRef}
          $bottomPadding={messengerBottomPadding}
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
        >
          {isLoadingMessages ? (
            <SvgLoadingAnimation />
          ) : allMessages.length === 0 ? (
            <MessengerPlaceholder>メッセージはまだありません</MessengerPlaceholder>
          ) : (
            <>
              {messagesToRender.map((msg, index) => {
                // `contract_concluded` は描画しない。締結状態は thread.status_timestamps
                // から導出して `contract_uploaded` 1 アイテムの見た目を切り替える。
                if (
                  msg.type === "system" &&
                  (msg.system_content?.kind === "contract_concluded" ||
                    msg.system_content?.kind === "escrow_locked" ||
                    msg.system_content?.kind === "escrow_released" ||
                    msg.system_content?.kind === "escrow_unlocked" ||
                    msg.system_content?.kind === "delivery_submitted")
                ) {
                  return null;
                }

                // 契約更新の提案。承認待ちのものだけ、提案された **その位置** に
                // 差分と導線を出す（成立・却下の告知より後に来ないよう時系列を保つ）。
                // 決着済みの提案は後続の結果メッセージが履歴を担うので描画しない。
                if (
                  msg.type === "system" &&
                  msg.system_content?.kind === "contract_amendment_proposed"
                ) {
                  if (!pendingAmendment || msg.id !== latestProposalMessageId) {
                    return null;
                  }
                  const isOursProposal = isOwnSideMessage(msg);
                  return (
                    <div
                      key={msg.id}
                      data-message-id={msg.id}
                      ref={(el) => registerMessageRef(msg.id, el)}
                    >
                      <ContractMessageRow
                        message={msg}
                        isOurs={isOursProposal}
                        showAvatar={!isOursProposal}
                        currentUserId={currentUserId}
                      >
                        <ContractAmendmentPanel
                          amendment={pendingAmendment}
                          role={isOursProposal ? "requester" : "contractor"}
                          onReview={handleReviewAmendment}
                          onWithdraw={() => {
                            void handleWithdrawAmendment();
                          }}
                          isBusy={isAmendmentBusy}
                          errorMessage={amendmentError}
                        />
                      </ContractMessageRow>
                    </div>
                  );
                }

                // 初期メッセージ / offer_summary は契約書アイテム内に統合表示するため
                // タイムライン上の独立行としては描画しない。
                if (mergedIntoContractIds.has(msg.id)) {
                  return null;
                }

                // 契約更新の成立は、初回のオファー成立と同じ並び
                // （システムメッセージの告知 → 契約アイテム）で見せる。
                if (
                  msg.type === "system" &&
                  msg.system_content?.kind === "contract_amended"
                ) {
                  const version = contractVersions.byAmendmentId.get(msg.id);
                  return (
                    <div
                      key={msg.id}
                      data-message-id={msg.id}
                      ref={(el) => registerMessageRef(msg.id, el)}
                    >
                      <ContractAmendedTimelineItem
                        message={msg}
                        content={msg.system_content}
                        isOurs={isOwnSideMessage(msg)}
                        showAvatar={!isOwnSideMessage(msg)}
                        currentUserId={currentUserId}
                        concludedAt={msg.created_at}
                        contractPdfPreview={version?.pdf ?? null}
                        isContractActive={version?.isActive ?? true}
                        onCloseRequest={() =>
                          logContractStubAction("クローズを相談")
                        }
                        onContractUpdate={() =>
                          setIsContractAmendModalOpen(true)
                        }
                        hasActiveChildSubcontracts={
                          thread.has_active_child_subcontracts
                        }
                        pendingAmendment={pendingAmendment}
                      />
                      {/* 初回が「契約書の提示 → オファー成立」の順なのに合わせ、
                          成立の告知は契約アイテムの下に置いて時系列を保つ。 */}
                      <TimelineNotice>
                        <SystemNoticePill>契約更新が成立</SystemNoticePill>
                      </TimelineNotice>
                    </div>
                  );
                }

                const isContractItem =
                  msg.type === "system" &&
                  msg.system_content?.kind === "contract_uploaded";
                const isSynthMessage = msg.id.startsWith(
                  SYNTH_CONTRACT_ID_PREFIX,
                );

                // 契約書アイテムは user メッセージスタイル（左右非対称）で描画する。
                // よって isSystem は contract_uploaded を含めない。
                const isSystem = msg.type === "system" && !isContractItem;
                const messageData = dmMessageToMessageData(msg);
                const isOurs = !isSystem && isOwnSideMessage(msg);
                const prevMsg = messagesToRender[index - 1];
                const prevIsUserLike =
                  !!prevMsg &&
                  (prevMsg.type !== "system" ||
                    prevMsg.system_content?.kind === "contract_uploaded");
                const isSameAuthorAsPrev =
                  !isSystem &&
                  prevIsUserLike &&
                  prevMsg!.author?.id === msg.author?.id;
                const parentMessage = msg.parent_id
                  ? messagesToRender.find((m) => m.id === msg.parent_id) || null
                  : null;
                const parentMessageData = parentMessage
                  ? dmMessageToMessageData(parentMessage)
                  : null;

                if (isContractItem) {
                  // BE の system_content に `expires_at` が含まれていればそれを採用、
                  // 無ければ thread.deadline で代替（subcontract の現行フロー想定）。
                  const expiresAt =
                    msg.system_content?.kind === "contract_uploaded"
                      ? msg.system_content.expires_at ?? thread.deadline
                      : thread.deadline;
                  return (
                    <div
                      key={msg.id}
                      data-message-id={msg.id}
                      ref={
                        isSynthMessage
                          ? undefined
                          : (el) => registerMessageRef(msg.id, el)
                      }
                    >
                      <ContractTimelineItem
                        message={msg}
                        isOurs={isOurs}
                        showAvatar={!isOurs && !isSameAuthorAsPrev}
                        currentUserId={currentUserId}
                        phase={contractCardPhase}
                        expiresAt={expiresAt}
                        concludedAt={contractConcludedAt}
                        initialMessage={
                          contractVersions.initial.snapshot.initial_message
                        }
                        offerSummary={contractVersions.initial.snapshot}
                        contractPdfPreview={contractVersions.initial.pdf}
                        isContractActive={contractVersions.initial.isActive}
                        onConfirm={handleViewOfferDetail}
                        onEdit={() => onRequestEditOffer?.(thread.id)}
                        onReject={() => setIsRejectConfirmOpen(true)}
                        onInvalidate={() => setIsCancelConfirmOpen(true)}
                        onCloseRequest={() =>
                          logContractStubAction("クローズを相談")
                        }
                        onContractUpdate={() =>
                          setIsContractAmendModalOpen(true)
                        }
                        pendingAmendment={pendingAmendment}
                        hasActiveChildSubcontracts={
                          thread.has_active_child_subcontracts
                        }
                      />
                    </div>
                  );
                }

                // 編集・削除可否（自メッセージ・user タイプ・cutoff 後のみ）
                const isAuthoredByMe = !!msg.author && msg.author.id === currentUserId;
                const canEdit = canEditDmMessage(msg, isAuthoredByMe, editCutoff);

                return (
                  <div
                    key={msg.id}
                    data-message-id={msg.id}
                    ref={(el) => registerMessageRef(msg.id, el)}
                  >
                    <MessageItem
                      message={messageData}
                      isOurs={isOurs}
                      isSystem={isSystem}
                      take={deliveryTakeNumbers.get(msg.id)}
                      showAvatar={!isSystem && !isSameAuthorAsPrev}
                      showActions={!isSystem}
                      parentMessage={parentMessageData}
                      currentUserId={currentUserId}
                      onReply={!isSystem ? handleReply : undefined}
                      onEdit={canEdit ? handleEdit : undefined}
                      onDelete={canEdit ? handleDelete : undefined}
                      onFileDownload={!isSystem ? handleFileDownload : undefined}
                      onTimestampClick={
                        !isSystem
                          ? (seconds, mediaItemId, frames) =>
                              handleTimestampClick(seconds, mediaItemId, msg.id, frames)
                          : undefined
                      }
                      onMediaRef={!isSystem ? handleMediaRef : undefined}
                      onPlayStateChange={!isSystem ? handlePlayStateChange : undefined}
                    />
                  </div>
                );
              })}
              {/* 検収完了後（subcontract）の相互レビュー。reviews は completed の
                  Detail レスポンスに含まれる（contractThreadDetail を流用）。 */}
              {threadType === "subcontract" &&
                thread.status === "completed" && (
                  <OfferReviewSection
                    projectId={projectId ?? ""}
                    dmId={thread.id}
                    reviews={contractThreadDetail?.data?.reviews}
                    viewerRole={
                      isRequesterViewer
                        ? "requester"
                        : isContractorViewer
                          ? "contractor"
                          : null
                    }
                    requesterName={thread.requester?.display_name ?? null}
                    contractorName={thread.contractor?.display_name ?? null}
                  />
                )}
              <MessageEndSpacer />
            </>
          )}
        </MessengerContent>
      </DropZoneOverlay>

      {/* アップロード進捗モーダル */}
      {uploadingFiles.length > 0 && (
        <Modal
          onClose={() => {
            const stillUploading = uploadingFiles.some(
              (f) => f.status === "uploading" || f.status === "pending"
            );
            if (!stillUploading) clearCompleted();
          }}
          size="default"
        >
          <UploadModalTitle>ファイルアップロード</UploadModalTitle>
          <UploadProgressBar
            files={uploadingFiles}
            onCancel={abortFile}
            onRetry={(file) => void retryFile(file.id)}
            compact={false}
            fitToParent
            postProcessLabel={postProcessLabel}
          />
        </Modal>
      )}

      {/* アップロード部分失敗の解決ダイアログ */}
      {uploadFailureDialog}

      {/* DmActionPanel は document.body に Portal で出す。
          理由: 祖先（TwoColumnLayout の SP 用ラッパ）に transform が設定されており、
          CSS 仕様上 transform 祖先がある `position: fixed` 子孫は viewport ではなく
          祖先要素がコンテナンブロックになる。これによって `bottom: 0` でも viewport
          下端に届かず ActionPanel が浮いて見える現象が SP で発生していた。
          Portal で body 直下に出すことで viewport を基準にした fixed が正しく機能する。
          横位置 (`left` / `width`) は useElementRect が `getBoundingClientRect` ベースで
          viewport 座標を返すため、そのまま使える。 */}
      {createPortal(
        <DmActionPanel
          ref={commentFieldRef}
          $bottom={actionPanelBottom}
          $left={panelRect.left}
          $width={panelRect.width}
        >
          {isContractorViewer && thread.status === "in_progress" && (
            <WorkflowActionRow>
              {workflowError && (
                <WorkflowErrorText>{workflowError}</WorkflowErrorText>
              )}
              <input
                ref={deliveryFileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = "";
                  if (files.length > 0) void handleDeliverFiles(files);
                }}
              />
              <Button
                size="medium"
                variant="filled"
                theme="primary"
                width="full"
                rightIcon="chevron_right"
                disabled={isSubmittingDelivery}
                onClick={() => deliveryFileInputRef.current?.click()}
              >
                成果物を提出
              </Button>
            </WorkflowActionRow>
          )}
          {isRequesterViewer &&
            (thread.status === "inspection_pending" ||
              thread.status === "in_progress") && (
              <WorkflowActionRow>
                {workflowError && (
                  <WorkflowErrorText>{workflowError}</WorkflowErrorText>
                )}
                <ApprovalControl
                  onConfirm={handleCompleteInspection}
                  disabled={isCompletingInspection}
                  pressLabel={
                    thread.status === "in_progress"
                      ? "長押しで検収（成果物の提出を待たずに完了します）"
                      : "長押しで検収"
                  }
                  swipeLabel={
                    thread.status === "in_progress"
                      ? "右にスワイプで検収（成果物の提出を待たずに完了します）"
                      : "右にスワイプで検収"
                  }
                />
                {thread.status === "inspection_pending" &&
                  thread.requires_deliverable !== false && (
                    <WorkflowRevertRow>
                      <Button
                        size="small"
                        variant="text"
                        theme="secondary"
                        disabled={isReverting}
                        onClick={() => setIsRevertConfirmOpen(true)}
                      >
                        差し戻す
                      </Button>
                    </WorkflowRevertRow>
                  )}
              </WorkflowActionRow>
            )}
          {/*
            契約書 PDF のダウンロードは、メッセージタイムライン内の
            「契約書アイテム」（ContractTimelineItem / ContractCard）に統合した。
            アクションパネル下に固定表示する従来の `OfferContractPdfDownloadRow`
            は廃止し、各アイテムの「ダウンロード」ボタンに置き換えている。
          */}
          <div className="u-w100 u-row-center">
            <TypingIndicator
              scope="dm"
              scopeKey={threadId}
              currentUserId={currentUserId}
              resolveUserName={resolveTypingUserName}
            />
          </div>
          <CommentField
            showPinIcon={false}
            showAttachIcon={true}
            onSend={handleSendMessage}
            editMode={!!messageToEdit}
            initialValue={messageToEdit?.message || ""}
            onCancel={messageToEdit ? cancelEdit : cancelReply}
            replyMode={!!messageToReply}
            replyToMessage={replyToMessageForField}
            onFilesSelected={handleFilesSelected}
            onFilesDroppedWithPath={handleFolderDrop}
            selectedFiles={allSelectedFiles}
            onRemoveFile={handleRemoveFile}
            multipleFiles={true}
            showIconsInEditMode={true}
            existingFiles={messageToEdit ? editExistingFiles : undefined}
            onRemoveExistingFile={messageToEdit ? handleRemoveExistingFile : undefined}
            onTypingChange={onTypingChange}
            onTypingCommit={onTypingCommit}
            enableChikuchiku={isDmSenderSide}
            chikuchikuScene="chikuchiku_filter_dm"
            chikuchikuContext={{
              project_id: projectId,
              dm_id: threadId,
              exclude_comment_id: messageToEdit?.id ?? null,
            }}
            enableReplyAssistant={isDmReceiverSide}
            replyAssistantScene="reply_assistant_dm"
            replyAssistantContext={{
              project_id: projectId,
              dm_id: threadId,
              exclude_comment_id: messageToEdit?.id ?? null,
            }}
            initialProofread={messageToEdit?.proofread ?? null}
          />
        </DmActionPanel>,
        document.body,
      )}

      {/* 削除確認ダイアログ */}
      {messageToDelete && (
        <ConfirmModal
          message="このメッセージを削除しますか？"
          confirmLabel="削除"
          loading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}

      {/* オファー受領モーダル（contractor 視点 / status: contractor_pending のみ） */}
      {showOfferReceiveModalInitially && isOfferReceiveModalOpen && projectId && (
        <OfferReceiveModal
          thread={thread}
          projectId={projectId}
          onClose={handleDismissOfferModal}
          onAccept={() => {
            void handleAcceptOffer();
          }}
          isAccepting={isAccepting}
          errorMessage={acceptError}
        />
      )}

      {/* 契約書「内容を確認」モーダル（タイムラインの契約書アイテムから開く / readOnly）。
          contractor_pending の自動受領モーダルと両立できるよう、ガード条件を独立に持つ。 */}
      {isContractReadOnlyModalOpen && projectId && (
        <OfferReceiveModal
          thread={thread}
          projectId={projectId}
          onClose={handleCloseContractReadOnly}
          onAccept={handleCloseContractReadOnly}
          readOnly
        />
      )}

      {/* 差し戻し確認ダイアログ */}
      {isRevertConfirmOpen && (
        <ConfirmModal
          message={"進行中に差し戻しますか？\n受託者に成果物の修正を依頼し、進行中の状態に戻します。"}
          confirmLabel="差し戻す"
          loading={isReverting}
          onConfirm={handleRevertToInProgress}
          onCancel={() => setIsRevertConfirmOpen(false)}
        />
      )}

      {/* 締結後の契約更新を提案するモーダル（委託者・3 点メニューから開く） */}
      {isContractAmendModalOpen && projectId && (
        <ContractAmendModal
          thread={thread}
          projectId={projectId}
          onClose={() => {
            setIsContractAmendModalOpen(false);
            setAmendmentError(null);
          }}
          onSubmit={(changes) => {
            void handleProposeAmendment(changes);
          }}
          isSubmitting={isProposingAmendment}
          errorMessage={amendmentError}
        />
      )}

      {/* 契約更新の承認モーダル（受託者・長押し承認 / 却下）。
          初回オファーの受領（OfferReceiveModal）と同じ作法に揃える。 */}
      {isAmendmentReviewOpen && pendingAmendment && (
        <ContractAmendmentReviewModal
          amendment={pendingAmendment}
          onClose={() => {
            setIsAmendmentReviewOpen(false);
            setAmendmentError(null);
          }}
          onApprove={() => {
            void handleRespondAmendment("accepted");
          }}
          onReject={() => {
            void handleRespondAmendment("rejected");
          }}
          isProcessing={isRespondingAmendment}
          errorMessage={amendmentError}
        />
      )}

      {/* オファーのキャンセル確認ダイアログ（送信者） */}
      {isCancelConfirmOpen && (
        <ConfirmModal
          message={"このオファーをキャンセルしますか？\n受託者に通知されます。"}
          confirmLabel="キャンセルする"
          cancelLabel="戻る"
          loading={isCancelling}
          onConfirm={handleConfirmCancel}
          onCancel={() => setIsCancelConfirmOpen(false)}
        />
      )}

      {/* オファーの辞退確認ダイアログ（受託者） */}
      {isRejectConfirmOpen && (
        <ConfirmModal
          message="このオファーを辞退しますか？"
          confirmLabel="辞退する"
          loading={isRejecting}
          onConfirm={handleConfirmReject}
          onCancel={() => setIsRejectConfirmOpen(false)}
        />
      )}

    </MessengerContainer>
  );
};

// ===== DM メインコンポーネント =====

const DM: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatchRoot = useDispatch();

  // スレッド一覧取得（再委託スレッドは BE 側で参加者フィルタ済み）
  // メール通知等から ?thread=<id> でディープリンクするため、URL→state 同期にも
  // 一覧データが必要なので DM 本体で取得し、ThreadList へ props で渡す。
  const { data: threadsData, isLoading: isLoadingThreads } = useGetDmThreadsQuery(
    { project_id: projectId!, sort_by: "modified", order: "desc" },
    { skip: !projectId }
  );
  const allThreads = useMemo(
    () => threadsData?.data?.threads ?? [],
    [threadsData]
  );

  // Chat（オファー非依存・ステータスなし）スレッド一覧。既存スレッド一覧に
  // マージして ThreadList に渡す。
  const { data: chatThreadsData } = useGetChatThreadsQuery(
    { project_id: projectId! },
    { skip: !projectId }
  );
  const chatThreads = useMemo(
    () => chatThreadsData?.data?.threads ?? [],
    [chatThreadsData]
  );

  const { data: currentUserData } = useGetCurrentUserQuery();
  const currentUserId = currentUserData?.data?.id;

  // セグメントコントロール（進行中 / 完了 / 検索）の状態は ThreadList へ
  // controlled で渡すため DM 親で保持する。
  const [filterMode, setFilterMode] = useState<FilterMode>("in_progress");
  const [searchText, setSearchText] = useState("");

  // ⌥5 再押下（既に DM 表示中）: セグメント（進行中 ↔ 完了）を切り替える。
  // 完了スレッドが無い場合は ThreadList 側の補正 effect が進行中へ戻す。
  useShortcutAction("dm-cycle", () => {
    setFilterMode((prev) =>
      prev === "in_progress" ? "completed" : "in_progress",
    );
  });

  // subcontract のドラフトスレッド（status: "draft" / "new"）は Redux ウィザード
  // state にハイドレートして ThreadList の Draft 表示に流す。BE は新 API では
  // ``'draft'`` を返すが、後方互換のため両値を ``isOfferDraftStatus`` で判定する。
  // 通常スレッド一覧からは除外する。
  useEffect(() => {
    if (!projectId) return;
    allThreads.forEach((thread) => {
      if (!isOfferDraftStatus(thread.status)) return;
      if (thread.thread_type !== "subcontract") return;
      // requester 本人にしか可視ではないため、追加チェックは不要
      dispatchRoot(hydrateDraftFromServer({ thread, projectId }));
    });
  }, [allThreads, projectId, dispatchRoot]);

  // ThreadList に渡すスレッドからはドラフト（"draft" / "new"）を除外
  // （左カラムのドラフトセクションで別表示されるため）。
  const visibleThreads = useMemo(
    () => allThreads.filter((t) => !isOfferDraftStatus(t.status)),
    [allThreads],
  );

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedDraftKey, setSelectedDraftKey] = useState<string | null>(null);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [showRightColumn, setShowRightColumn] = useState(false);
  const [layoutSetShowRightColumn, setLayoutSetShowRightColumn] = useState<
    ((show: boolean) => void) | null
  >(null);

  // プロジェクト切替時の選択状態クリア。
  // ルートが同じ `/:projectId` のため、別プロジェクトに遷移しても DM コンポーネント
  // 自体は再マウントされず、`selectedThreadId` / `selectedDraftKey` に前プロジェクトの
  // 値が残ってしまう。残ったまま state→URL effect が走ると、新プロジェクトの URL に
  // 古い `?thread=<前プロジェクトID>` が書き戻されてしまい、URL→state effect の
  // `selectedThreadId === threadIdFromUrl` ガードで以後の反映が止まる事故が起きる。
  // ここで projectId 変化を検知して状態をクリアし、URL→state effect に追従させる。
  useEffect(() => {
    setSelectedThreadId(null);
    setSelectedDraftKey(null);
    setSelectedChatId(null);
  }, [projectId]);

  // ドラフト一覧を購読しておき、選択中ドラフトが破棄されたら選択解除する
  const drafts = useSelector(selectOfferDraftsByProject(projectId));
  useEffect(() => {
    if (!selectedDraftKey) return;
    const stillExists = drafts.some((d) => d.threadKey === selectedDraftKey);
    if (!stillExists) {
      setSelectedDraftKey(null);
    }
  }, [drafts, selectedDraftKey]);

  // 選択中スレッドの本体オブジェクト（threadType / status_timestamps を Messenger に渡すため）
  const selectedThread = useMemo(
    () => allThreads.find((t) => t.id === selectedThreadId) ?? null,
    [allThreads, selectedThreadId]
  );
  const selectedChat = useMemo(
    () => chatThreads.find((c) => c.id === selectedChatId) ?? null,
    [chatThreads, selectedChatId]
  );

  // 選択スレッドの status に応じて filterMode（左カラムのセグメントコントロール）を
  // 整合させる保険。以下のように、選択スレッドとフィルタタブが食い違うケースを補正する:
  //
  // - 「完了」タブ表示中に新規オファーを送信した直後
  //   （送信スレッドは contractor_pending = 進行中グループ）
  // - メール通知などからの ?thread=<id> deep-link
  //   （現在のフィルタタブと該当スレッドのグループが一致しないことがある）
  //
  // 補正は「選択スレッドが実際に切り替わったとき」だけ行う。`filterMode` も deps に
  // 含むため、ユーザーが手動でタブを切り替えただけ（選択スレッドは不変）のときにも
  // この effect は再発火するが、その場合に選択スレッドのグループへタブを引き戻すと、
  // 完了スレッド選択中に「進行中」を押しても一瞬で「完了」へ戻ってしまう不具合になる。
  // そこで前回補正した時点の `id:status` を ref で覚えておき、同じ組み合わせのときは
  // 再補正しない（= ユーザーのタブ手動操作を尊重する）。`id` だけでなく `status` も
  // 含めるのは、Messenger 上での検収完了（inspection_pending → completed）等で
  // 「選択スレッドのまま状態だけ変わった」場合にもタブを追従させるため。
  // 検索モードはユーザーの明示操作で入っているため維持する。
  const alignedThreadKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const currentKey = selectedThread
      ? `${selectedThread.id}:${selectedThread.status}`
      : null;
    const prevKey = alignedThreadKeyRef.current;
    alignedThreadKeyRef.current = currentKey;

    if (currentKey === prevKey) return;
    if (!selectedThread) return;
    if (filterMode === "search") return;
    const status = selectedThread.status;
    if (
      (IN_PROGRESS_STATUSES as readonly string[]).includes(status) &&
      filterMode !== "in_progress"
    ) {
      setFilterMode("in_progress");
    } else if (
      (COMPLETED_STATUSES as readonly string[]).includes(status) &&
      filterMode !== "completed"
    ) {
      setFilterMode("completed");
    }
  }, [selectedThread, filterMode]);

  // 編集モードで開いているドラフトの元スレッド。contractor_pending なら
  // 送信済みオファーの内容編集（OfferWizardOverlay に editMode を渡す）。
  const editingDraftThread = useMemo(
    () =>
      allThreads.find((t) => dmThreadKey(t.id) === selectedDraftKey) ?? null,
    [allThreads, selectedDraftKey]
  );
  const isEditingExistingOffer =
    editingDraftThread?.status === "contractor_pending";

  const getSetShowRightColumn = useCallback(
    (fn: (show: boolean) => void) => {
      setLayoutSetShowRightColumn(() => fn);
    },
    []
  );

  const showRightSide = useCallback(() => {
    if (layoutSetShowRightColumn) {
      layoutSetShowRightColumn(true);
    } else {
      setShowRightColumn(true);
    }
  }, [layoutSetShowRightColumn]);

  const dispatch = useDispatch();
  const handleThreadSelect = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      // 既存スレッドを選択した瞬間、開いているドラフトは最小化（状態は保持）
      if (selectedDraftKey) {
        dispatch(closeDraft({ threadKey: selectedDraftKey }));
      }
      setSelectedDraftKey(null);
      setSelectedChatId(null);
      showRightSide();
    },
    [dispatch, showRightSide, selectedDraftKey],
  );

  const handleChatSelect = useCallback(
    (chatId: string) => {
      setSelectedChatId(chatId);
      setSelectedThreadId(null);
      if (selectedDraftKey) {
        dispatch(closeDraft({ threadKey: selectedDraftKey }));
      }
      setSelectedDraftKey(null);
      showRightSide();
    },
    [dispatch, showRightSide, selectedDraftKey],
  );

  const handleDraftSelect = useCallback(
    (threadKey: string) => {
      // 既存スレッド選択を解除し、ドラフトのオーバーレイを開く
      setSelectedThreadId(null);
      setSelectedChatId(null);
      setSelectedDraftKey(threadKey);
      dispatch(openDraft({ threadKey }));
      showRightSide();
    },
    [dispatch, showRightSide],
  );

  // OfferWizardOverlay 内で `assignServerDmId` によって threadKey が
  // `draft:<uuid>` → `dm:<dm_id>` にリネームされた直後の追従。
  const handleThreadKeyAssigned = useCallback((newThreadKey: string) => {
    setSelectedDraftKey(newThreadKey);
  }, []);

  // オファー送信成功時: ドラフトを閉じ、該当スレッドを選択してメッセンジャー表示に切り替える。
  // 送信遷移後のスレッドは contractor_pending（= 進行中グループ）になるため、
  // 「完了」タブ表示中に新規オファーを送ると左カラムから消えて見える事象が起きる。
  // 送信完了時に必ず `in_progress` モードへ戻し、送信スレッドが一覧に出るようにする。
  const handleOfferSubmitted = useCallback(
    (dmId: string) => {
      setSelectedDraftKey(null);
      setSelectedThreadId(dmId);
      setFilterMode("in_progress");
      showRightSide();
    },
    [showRightSide],
  );

  // 契約アイテムの「内容を編集」: 送信済み（contractor_pending）オファーを
  // ウィザードに復元し、編集モード（form ステップ）で開く。
  const handleRequestEditOffer = useCallback(
    (dmId: string) => {
      const thread = allThreads.find((t) => t.id === dmId);
      if (!thread || !projectId) return;
      dispatchRoot(hydrateDraftFromServer({ thread, projectId }));
      const key = dmThreadKey(dmId);
      dispatchRoot(setStep({ threadKey: key, step: "form" }));
      setSelectedThreadId(null);
      setSelectedDraftKey(key);
      dispatchRoot(openDraft({ threadKey: key }));
      showRightSide();
    },
    [allThreads, projectId, dispatchRoot, showRightSide],
  );

  // URL ↔ state 同期の循環反応を防ぐための ref。
  // - `lastAppliedUrlThreadRef`: URL→state effect が「最後に解釈した URL の thread 値」。
  //   外部 navigate で URL が変わるたびに更新し、state→URL effect が同じ値で
  //   書き戻すケースでは「URL に変化なし」とみなして state へ再反映しない。
  // - `searchParamsRef`: state→URL effect で最新の URL を読むための ref。
  //   state→URL の deps から `searchParams` を外すことで、URL→state effect が
  //   URL を書き換えた直後に state→URL がクロージャの古い `selectedThreadId` で
  //   書き戻す競合（振動）を断ち切る。
  const lastAppliedUrlThreadRef = useRef<string | null>(null);
  const lastAppliedUrlChatRef = useRef<string | null>(null);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  // (1) URL → state: ?thread=<id> を state に反映（threads 取得後に検証）
  // ディープリンクは「URLにthread=がある・スレッド一覧がロード済みで該当が存在する・
  // TwoColumnLayout の setShowRightColumn が公開されている」が揃った時点で適用する
  // （モバイルで右カラムをスライドインさせる必要があるため、
  // layoutSetShowRightColumn の到着を待つ）。
  //
  // グローバルメニューの進行中モーダル等から「同一プロジェクト内 / 別プロジェクト
  // 内に既にいる状態」で `?thread=<別 id>` にディープリンクされたとき、ルートが
  // 同じ `/:projectId` のため再マウントは起きない。1回限りのガードだとスレッド
  // 切替が反映されないため、URL の thread 値が前回適用時から変化していれば
  // 再適用する（`lastAppliedUrlThreadRef` で重複適用を防ぐ）。
  // ドラフト（status === "draft" / "new"）の場合はメッセンジャーではなく
  // ウィザードオーバーレイを開く必要があるので、`selectedDraftKey` を立てる。
  useEffect(() => {
    if (!layoutSetShowRightColumn) return;
    const threadIdFromUrl = searchParams.get("thread");
    // 前回適用した URL 値と同じなら、ユーザー操作経由の state→URL 反映も含めて
    // ここでは反応しない（ユーザー側 setSelectedThreadId を巻き戻さない）。
    if (lastAppliedUrlThreadRef.current === threadIdFromUrl) return;

    if (!threadIdFromUrl) {
      lastAppliedUrlThreadRef.current = threadIdFromUrl;
      return;
    }
    // すでに同じスレッド / ドラフトが選択済みなら ref だけ進めて以後 skip。
    if (selectedThreadId === threadIdFromUrl) {
      lastAppliedUrlThreadRef.current = threadIdFromUrl;
      return;
    }
    const expectedDraftKey = dmThreadKey(threadIdFromUrl);
    if (selectedDraftKey === expectedDraftKey) {
      lastAppliedUrlThreadRef.current = threadIdFromUrl;
      return;
    }
    const found = allThreads.find((t) => t.id === threadIdFromUrl);
    // `allThreads` 未取得のときは ref を進めず、取得完了後の effect 再走で再試行する。
    if (!found) return;

    lastAppliedUrlThreadRef.current = threadIdFromUrl;
    if (isOfferDraftStatus(found.status) && found.thread_type === "subcontract") {
      setSelectedThreadId(null);
      setSelectedDraftKey(expectedDraftKey);
      dispatch(openDraft({ threadKey: expectedDraftKey }));
    } else {
      // 既存ドラフトを最小化してから既存スレッドを選択。
      if (selectedDraftKey) {
        dispatch(closeDraft({ threadKey: selectedDraftKey }));
        setSelectedDraftKey(null);
      }
      setSelectedThreadId(found.id);
    }
    layoutSetShowRightColumn(true);
  }, [
    searchParams,
    allThreads,
    layoutSetShowRightColumn,
    dispatch,
    selectedThreadId,
    selectedDraftKey,
  ]);

  // (2) state → URL: DM タブに居るときだけ ?thread= を同期
  // 注: selectedThreadId が null のケースでは URL を書き換えない。
  // 初期マウント時にスレッド一覧がロード完了する前にエフェクトが走ると
  // URL から thread= が消えてしまい、ディープリンクが成立しなくなるため。
  // 追加ガード: `selectedThreadId` が現在の `allThreads` に存在しないとき
  // （プロジェクト切替直後にリセット effect が走るまでの間や、参加権限を
  // 失ったスレッド等）は URL を書き換えない。古いスレッド ID で
  // ディープリンクの `?thread=` を上書きしてしまうのを防ぐ。
  //
  // deps からは `searchParams` を外す。`searchParams` が変化したときに本 effect
  // が走ると、クロージャの古い `selectedThreadId` で URL を書き戻してしまい、
  // URL→state effect との間で振動が起きるため。URL の現在値は ref 経由で読む。
  useEffect(() => {
    if (!selectedThreadId) return;
    if (!allThreads.some((t) => t.id === selectedThreadId)) return;
    const params = searchParamsRef.current;
    if (params.get("tab") !== "dm") return;
    if (params.get("thread") === selectedThreadId) return;
    // 自分で書き換えた URL を URL→state effect が拾って巻き戻さないように、
    // ref を先に更新してから setSearchParams する。
    lastAppliedUrlThreadRef.current = selectedThreadId;
    const next = new URLSearchParams(params);
    next.set("tab", "dm");
    next.set("thread", selectedThreadId);
    setSearchParams(next, { replace: true });
  }, [selectedThreadId, allThreads, setSearchParams]);

  // (3) URL → state: ?chat=<id> を Chat スレッド選択へ反映（通知メール等のディープリンク）。
  // Chat の選択は一覧クリックが主体のため、ここでは URL→state の一方向同期のみ行い、
  // 選択中 Chat を URL へ書き戻すことはしない（?thread= 同期との競合・振動を避ける）。
  useEffect(() => {
    if (!layoutSetShowRightColumn) return;
    const chatIdFromUrl = searchParams.get("chat");
    if (lastAppliedUrlChatRef.current === chatIdFromUrl) return;
    if (!chatIdFromUrl) {
      lastAppliedUrlChatRef.current = chatIdFromUrl;
      return;
    }
    if (selectedChatId === chatIdFromUrl) {
      lastAppliedUrlChatRef.current = chatIdFromUrl;
      return;
    }
    const found = chatThreads.find((c) => c.id === chatIdFromUrl);
    // 一覧未取得のときは ref を進めず、取得完了後の再走で再試行する。
    if (!found) return;
    lastAppliedUrlChatRef.current = chatIdFromUrl;
    if (selectedDraftKey) {
      dispatch(closeDraft({ threadKey: selectedDraftKey }));
      setSelectedDraftKey(null);
    }
    setSelectedThreadId(null);
    setSelectedChatId(chatIdFromUrl);
    layoutSetShowRightColumn(true);
  }, [
    searchParams,
    chatThreads,
    layoutSetShowRightColumn,
    dispatch,
    selectedChatId,
    selectedDraftKey,
  ]);

  return (
    <DmTwoColumnWrapper>
      <TwoColumnLayout
        leftColumnContent={
          <ThreadList
            selectedThreadId={selectedThreadId}
            selectedDraftKey={selectedDraftKey}
            selectedChatId={selectedChatId}
            onThreadSelect={handleThreadSelect}
            onDraftSelect={handleDraftSelect}
            onChatSelect={handleChatSelect}
            threads={visibleThreads}
            chatThreads={chatThreads}
            isLoading={isLoadingThreads}
            filterMode={filterMode}
            onFilterModeChange={setFilterMode}
            searchText={searchText}
            onSearchTextChange={setSearchText}
          />
        }
        rightColumnContent={
          selectedChat ? (
            <ChatMessenger
              thread={selectedChat}
              currentUserId={currentUserId}
            />
          ) : selectedDraftKey && projectId ? (
            <DraftRightColumn>
              <OfferWizardOverlay
                threadKey={selectedDraftKey}
                projectId={projectId}
                onThreadKeyAssigned={handleThreadKeyAssigned}
                onSubmitted={handleOfferSubmitted}
                editMode={isEditingExistingOffer}
              />
            </DraftRightColumn>
          ) : selectedThread ? (
            isOfferDraftingForViewer(selectedThread, currentUserId) ? (
              <DraftRightColumn>
                <OfferWizardOverlay
                  threadKey={dmThreadKey(selectedThread.id)}
                  projectId={projectId!}
                  onThreadKeyAssigned={handleThreadKeyAssigned}
                  onSubmitted={handleOfferSubmitted}
                />
              </DraftRightColumn>
            ) : (
              <Messenger
                thread={selectedThread}
                showOfferReceiveModalInitially={isOfferReceivePendingForViewer(
                  selectedThread,
                  currentUserId,
                )}
                onRequestEditOffer={handleRequestEditOffer}
              />
            )
          ) : null
        }
        initialShowRightColumn={showRightColumn}
        getSetShowRightColumn={getSetShowRightColumn}
        hasProjectBanner={true}
        topRightOverlay={<ProjectMenu />}
      />
    </DmTwoColumnWrapper>
  );
};

export default DM;
